// src/modules/workspace/NLPLab.jsx
// ─────────────────────────────────────────────────────────────────────────────
// NLP LAB — each feature is a top-level tab routed via subTab prop.
// NO internal sub-nav. Follows exact same pattern as MLLab.
//
// ExperimentWorkspace routes:
//   nlp_setup      → subTab="setup"
//   nlp_stats      → subTab="stats"
//   nlp_frequency  → subTab="frequency"
//   nlp_readability→ subTab="readability"
//   nlp_sentiment  → subTab="sentiment"
//   nlp_entities   → subTab="entities"
//   nlp_keywords   → subTab="keywords"
//   nlp_topics     → subTab="topics"
//   nlp_summary    → subTab="summary"
//   nlp_similarity → subTab="similarity"
//   report (nlp_qa)→ subTab="report"
// ─────────────────────────────────────────────────────────────────────────────
import React, { useState, useMemo, useCallback, useRef } from 'react';
import {
  BarChart, Bar, LineChart, Line, AreaChart, Area,
  ScatterChart, Scatter, RadarChart, Radar,
  PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  PieChart, Pie,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell,
} from 'recharts';
import {
  C, PALETTE, inferTypes, Btn, Label, ChartTip, PinButton,
} from './wsShared';

// ─────────────────────────────────────────────────────────────────────────────
// PURE-JS NLP ENGINE  (zero external deps)
// ─────────────────────────────────────────────────────────────────────────────
const STOPWORDS = new Set([
  'the','a','an','and','or','but','in','on','at','to','for','of','with','by','from',
  'up','about','into','through','is','are','was','were','be','been','being','have',
  'has','had','do','does','did','will','would','could','should','may','might','can',
  'i','you','he','she','it','we','they','me','him','her','us','them','this','that',
  'these','those','what','which','who','how','all','each','every','both','not','no',
  'so','yet','also','just','very','still','than','then','if','as','its','my','your',
  'his','our','their','more','most','other','some','any','now','here','there','am',
  'one','two','said','get','got','say','make','like','see','know','go','been',
]);
const SENT_POS = new Set(['good','great','excellent','amazing','wonderful','fantastic','superb',
  'love','best','perfect','beautiful','awesome','outstanding','brilliant','happy','joy',
  'positive','success','win','helpful','useful','clear','easy','fast','powerful','effective',
  'innovative','smart','nice','pleasant','enjoy','recommend','impressive','strong','better',
  'improved','benefit','valuable','trust','safe','reliable','free','friendly','clean','glad',
  'pleased','excited','proud','grateful','remarkable','ideal','superior','delightful']);
const SENT_NEG = new Set(['bad','terrible','awful','horrible','worst','hate','poor','wrong',
  'fail','failure','error','bug','broken','slow','difficult','hard','confusing','unclear',
  'useless','worthless','boring','ugly','weak','negative','problem','issue','crash','loss',
  'lose','disappoint','pain','struggle','complex','mess','toxic','harm','damage','risk',
  'danger','fear','angry','anger','sad','unhappy','impossible','never','cannot','worse',
  'expensive','missing','lack','failed','annoying','frustrating','inadequate','inferior',
  'dreadful','appalling','mediocre','flawed','defective','corrupt','spam','abuse']);
const INTENSIFIERS = new Set(['very','really','extremely','absolutely','totally','highly',
  'quite','super','incredibly','remarkably','exceptionally','particularly','especially']);
const NEGATORS = new Set(['not','no','never','none','nobody','nothing','neither','nor',
  'hardly','barely','without','lack','lacking']);

function tokenize(text, sw = true) {
  const w = String(text || '')
    .toLowerCase()
    .replace(/https?:\/\/\S+/g, ' ')
    .replace(/[^a-z0-9\s]/g, ' ')
    .split(/\s+/)
    .filter(w => w.length > 1);
  return sw ? w.filter(w => !STOPWORDS.has(w)) : w;
}

function tokenizeSentences(text) {
  return String(text || '')
    .split(/(?<=[.!?])\s+(?=[A-Z"'])|(?<=[.!?])\s*\n/)
    .map(s => s.trim())
    .filter(s => s.length > 10);
}

function sentimentScore(text) {
  const words = String(text || '').toLowerCase().replace(/[^a-z\s]/g, ' ').split(/\s+/);
  let score = 0, count = 0, negated = false;
  words.forEach((w, i) => {
    if (NEGATORS.has(w)) { negated = true; return; }
    const boost = i > 0 && INTENSIFIERS.has(words[i - 1]) ? 1.5 : 1;
    if (SENT_POS.has(w)) { score += negated ? -boost : boost; count++; negated = false; }
    if (SENT_NEG.has(w)) { score += negated ?  boost : -boost; count++; negated = false; }
  });
  const norm = count > 0 ? Math.max(-1, Math.min(1, score / (count + Math.sqrt(count)))) : 0;
  return {
    score: +norm.toFixed(4),
    label: norm > 0.15 ? 'positive' : norm < -0.15 ? 'negative' : 'neutral',
    posCount: words.filter(w => SENT_POS.has(w)).length,
    negCount: words.filter(w => SENT_NEG.has(w)).length,
  };
}

function nGrams(tokens, n) {
  const g = {};
  for (let i = 0; i <= tokens.length - n; i++) {
    const k = tokens.slice(i, i + n).join(' ');
    g[k] = (g[k] || 0) + 1;
  }
  return Object.entries(g).sort((a, b) => b[1] - a[1]);
}

function computeTFIDF(docs) {
  const N = docs.length, df = {};
  docs.forEach(t => { const u = new Set(t); u.forEach(w => { df[w] = (df[w] || 0) + 1; }); });
  return docs.map(toks => {
    const tf = {};
    toks.forEach(t => { tf[t] = (tf[t] || 0) + 1; });
    const out = {};
    Object.entries(tf).forEach(([t, f]) => {
      out[t] = +((f / toks.length) * (Math.log((N + 1) / ((df[t] || 0) + 1)) + 1)).toFixed(6);
    });
    return out;
  });
}

function cosineSim(v1, v2) {
  const keys = new Set([...Object.keys(v1), ...Object.keys(v2)]);
  let dot = 0, n1 = 0, n2 = 0;
  keys.forEach(k => { dot += (v1[k]||0)*(v2[k]||0); n1 += (v1[k]||0)**2; n2 += (v2[k]||0)**2; });
  return n1 && n2 ? +(dot / Math.sqrt(n1 * n2)).toFixed(4) : 0;
}

function getBasicStats(texts) {
  const all = texts.join(' ');
  const sentences = texts.flatMap(t => tokenizeSentences(t));
  const allWords = tokenize(all, false);
  const uniqueWords = new Set(allWords);
  return {
    words: allWords.length,
    unique: uniqueWords.size,
    sentences: sentences.length,
    paragraphs: texts.filter(t => t.length > 20).length,
    avgWordLen: +(allWords.reduce((s, w) => s + w.length, 0) / Math.max(allWords.length, 1)).toFixed(1),
    avgSentLen: +(allWords.length / Math.max(sentences.length, 1)).toFixed(1),
    lexDiv: +(uniqueWords.size / Math.max(allWords.length, 1)).toFixed(3),
    readTime: Math.ceil(allWords.length / 200),
    chars: all.length,
  };
}

function fleschAll(texts) {
  const text = texts.slice(0, 100).join('. ');
  const sents = Math.max(1, (text.match(/[.!?]+/g) || []).length);
  const words = text.split(/\s+/).filter(w => w.length > 0);
  const wc = Math.max(1, words.length);
  const syl = words.reduce((s, w) => s + Math.max(1, (w.toLowerCase().match(/[aeiouy]+/g) || []).length), 0);
  const flesch = +(206.835 - 1.015 * (wc / sents) - 84.6 * (syl / wc)).toFixed(1);
  const fkGrade = +(0.39 * (wc / sents) + 11.8 * (syl / wc) - 15.59).toFixed(1);
  const fog = +(0.4 * (wc / sents + 100 * (words.filter(w => w.length > 6).length / wc))).toFixed(1);
  const smog = +(3 + Math.sqrt(words.filter(w => w.length > 6).length * (30 / sents))).toFixed(1);
  const ari = +(4.71 * (text.replace(/\s/g, '').length / wc) + 0.5 * (wc / sents) - 21.43).toFixed(1);
  const grade = flesch >= 90 ? 'Very Easy' : flesch >= 70 ? 'Easy' : flesch >= 60 ? 'Standard' : flesch >= 50 ? 'Fairly Difficult' : flesch >= 30 ? 'Difficult' : 'Very Difficult';
  const complexWords = words.filter(w => w.length > 6).length;
  return { flesch, fkGrade, fog, smog, ari, grade, awps: +(wc / sents).toFixed(1), asw: +(syl / wc).toFixed(2), complexWords, syl, complexPct: +(complexWords / wc * 100).toFixed(1) };
}

function detectLang(text) {
  const lower = text.toLowerCase();
  const langs = [
    { name: 'English',    words: ['the','and','is','of','in','that','for'] },
    { name: 'Spanish',    words: ['el','la','de','que','en','los','una'] },
    { name: 'French',     words: ['le','la','les','de','et','un','une'] },
    { name: 'German',     words: ['der','die','das','und','ist','ein'] },
    { name: 'Portuguese', words: ['de','o','a','e','que','em','um'] },
    { name: 'Italian',    words: ['il','la','di','che','in','un','per'] },
  ];
  const best = langs.map(l => ({ ...l, score: l.words.filter(w => new RegExp(`\\b${w}\\b`).test(lower)).length }))
    .sort((a, b) => b.score - a.score)[0];
  return best.score > 0 ? best.name : 'Unknown';
}

function getWordLengthDist(texts) {
  const dist = {};
  tokenize(texts.join(' '), false).forEach(w => { dist[w.length] = (dist[w.length] || 0) + 1; });
  return Object.entries(dist).map(([len, count]) => ({ len: +len, count })).sort((a, b) => a.len - b.len);
}

function getSentLengthDist(texts) {
  return texts.flatMap(t => tokenizeSentences(t)).slice(0, 80)
    .map((s, i) => ({ idx: i + 1, words: s.split(/\s+/).filter(Boolean).length }));
}

function getCharAnalysis(texts) {
  const all = texts.join('');
  const lower = all.toLowerCase();
  const letters = lower.match(/[a-z]/g) || [];
  const freq = {};
  letters.forEach(c => { freq[c] = (freq[c] || 0) + 1; });
  const charFreq = Object.entries(freq).sort((a, b) => b[1] - a[1]).map(([char, count]) => ({ char, count }));
  const vowels = (lower.match(/[aeiou]/g) || []).length;
  const consonants = (lower.match(/[bcdfghjklmnpqrstvwxyz]/g) || []).length;
  const entropy = charFreq.reduce((e, { count }) => { const p = count / Math.max(letters.length, 1); return e - p * Math.log2(p + 1e-9); }, 0);
  return {
    lowercase: (all.match(/[a-z]/g) || []).length,
    uppercase: (all.match(/[A-Z]/g) || []).length,
    digits: (all.match(/\d/g) || []).length,
    punctuation: (all.match(/[.,!?;:'"()\[\]{}\-]/g) || []).length,
    spaces: (all.match(/\s/g) || []).length,
    vowels, consonants,
    vowelRatio: +(vowels / (vowels + consonants + 0.01) * 100).toFixed(1),
    entropy: +entropy.toFixed(3), charFreq,
  };
}

function getLinguisticDiversity(texts) {
  const all = texts.join(' ');
  const words = tokenize(all, false);
  const freq = {};
  words.forEach(w => { freq[w] = (freq[w] || 0) + 1; });
  const unique = Object.keys(freq).length;
  const hapax = Object.values(freq).filter(c => c === 1).length;
  const ttr = +(unique / Math.max(words.length, 1)).toFixed(3);
  const rttr = +(unique / Math.sqrt(Math.max(words.length, 1))).toFixed(3);
  const cttr = +(unique / Math.sqrt(2 * Math.max(words.length, 1))).toFixed(3);
  const herdanC = +(Math.log(unique) / Math.log(Math.max(words.length, 2))).toFixed(3);
  // Yule's K
  const freqOfFreq = {};
  Object.values(freq).forEach(c => { freqOfFreq[c] = (freqOfFreq[c] || 0) + 1; });
  const M1 = Object.entries(freqOfFreq).reduce((s, [r, f]) => s + +r * f, 0);
  const M2 = Object.entries(freqOfFreq).reduce((s, [r, f]) => s + +r * +r * f, 0);
  const yulesK = +((10000 * (M2 - M1)) / (M1 * M1 + 1e-9)).toFixed(2);
  // TTR curve
  const step = Math.max(1, Math.floor(words.length / 30));
  const ttrCurve = [];
  const seen = new Set();
  for (let i = 0; i < words.length; i += step) {
    words.slice(0, i + step).forEach(w => seen.add(w));
    ttrCurve.push({ pos: i + step, ttr: +(seen.size / (i + step)).toFixed(3) });
  }
  return { ttr, rttr, cttr, herdanC, yulesK, hapax, hapaxRatio: +(hapax / Math.max(unique, 1)).toFixed(3), unique, total: words.length, ttrCurve };
}

function extractEntities(texts) {
  const all = texts.join(' ');
  const PERSON   = (all.match(/\b(?:Mr|Mrs|Ms|Dr|Prof)\.?\s+[A-Z][a-z]+(?:\s+[A-Z][a-z]+)*/g) || []);
  const ORG      = (all.match(/\b[A-Z][a-z]*(?:\s+[A-Z][a-z]*)*(?:\s+(?:Inc|Ltd|LLC|Corp|Co|Group|Foundation|Institute|University|College|School|Hospital|Bank|Fund))\b/g) || []);
  const LOCATION = (all.match(/\b(?:in|from|at|near|to)\s+([A-Z][a-z]+(?:\s+[A-Z][a-z]+)*)/g) || []).map(m => m.replace(/^(?:in|from|at|near|to)\s+/, ''));
  const DATE     = (all.match(/\b(?:January|February|March|April|May|June|July|August|September|October|November|December)\s+\d{1,2}(?:,\s+\d{4})?|\b\d{1,2}[\/\-]\d{1,2}[\/\-]\d{2,4}\b/g) || []);
  const MONEY    = (all.match(/\$[\d,]+(?:\.\d+)?(?:\s*(?:million|billion|thousand))?|\b\d+(?:\.\d+)?\s*(?:USD|EUR|GBP|dollars?|euros?|pounds?)\b/gi) || []);
  const PERCENT  = (all.match(/\b\d+(?:\.\d+)?\s*%/g) || []);
  const EMAIL    = (all.match(/\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}\b/g) || []);
  const URL      = (all.match(/https?:\/\/[^\s]+/g) || []);
  const HASHTAG  = (all.match(/#[A-Za-z0-9_]+/g) || []);
  const MENTION  = (all.match(/@[A-Za-z0-9_]+/g) || []);
  const dedup = arr => [...new Set(arr)];
  return { PERSON: dedup(PERSON), ORG: dedup(ORG), LOCATION: dedup(LOCATION), DATE: dedup(DATE), MONEY: dedup(MONEY), PERCENT: dedup(PERCENT), EMAIL: dedup(EMAIL), URL: dedup(URL), HASHTAG: dedup(HASHTAG), MENTION: dedup(MENTION) };
}

function extractKeywords(texts, n = 25) {
  const docs = texts.slice(0, 300).map(t => tokenize(t, true));
  const vecs = computeTFIDF(docs);
  const agg = {};
  const freqMap = {};
  vecs.forEach(v => Object.entries(v).forEach(([w, s]) => { agg[w] = (agg[w] || 0) + s; }));
  tokenize(texts.join(' '), true).forEach(w => { freqMap[w] = (freqMap[w] || 0) + 1; });
  return Object.entries(agg).sort((a, b) => b[1] - a[1]).slice(0, n)
    .map(([word, tfidf]) => ({ word, tfidf: +tfidf.toFixed(4), freq: freqMap[word] || 0 }));
}

function extractTopics(texts, nTopics = 5) {
  const allToks = texts.map(t => tokenize(t, true).filter(w => w.length > 3));
  const vocab = {};
  allToks.forEach(toks => toks.forEach(w => { vocab[w] = (vocab[w] || 0) + 1; }));
  const topV = Object.entries(vocab).sort((a, b) => b[1] - a[1]).slice(0, 200).map(([w]) => w);
  const cooc = {};
  allToks.forEach(toks => {
    const uniq = [...new Set(toks)].filter(w => topV.includes(w));
    for (let i = 0; i < uniq.length; i++)
      for (let j = i + 1; j < uniq.length; j++) {
        const k = [uniq[i], uniq[j]].sort().join('|');
        cooc[k] = (cooc[k] || 0) + 1;
      }
  });
  const used = new Set(); const topics = [];
  for (let t = 0; t < nTopics; t++) {
    const seed = topV.find(w => !used.has(w));
    if (!seed) break;
    used.add(seed);
    const related = Object.entries(cooc)
      .filter(([k]) => k.includes(seed))
      .map(([k, v]) => ({ word: k.split('|').find(w => w !== seed), score: v }))
      .filter(x => x.word && !used.has(x.word))
      .sort((a, b) => b.score - a.score).slice(0, 9);
    const words = [seed, ...related.map(r => r.word)];
    words.forEach(w => used.add(w));
    const docCount = allToks.filter(toks => words.some(w => toks.includes(w))).length;
    topics.push({ id: t, words, docCount, label: `Topic ${t + 1}`, color: PALETTE[t % PALETTE.length] });
  }
  return topics;
}

function extractiveSummary(texts, n = 5) {
  const sentences = texts.flatMap(t => tokenizeSentences(t));
  const freq = {};
  tokenize(texts.join(' '), true).forEach(w => { freq[w] = (freq[w] || 0) + 1; });
  const scored = sentences.map((s, i) => {
    const toks = tokenize(s, true);
    return { idx: i, text: s, score: +(toks.reduce((a, w) => a + (freq[w] || 0), 0) / Math.max(toks.length, 1)).toFixed(3) };
  });
  return {
    sentences: scored.sort((a, b) => b.score - a.score).slice(0, n).sort((a, b) => a.idx - b.idx).map(s => s.text),
    scores: [...scored].sort((a, b) => b.score - a.score),
  };
}

function getRepetition(texts) {
  const allSents = texts.flatMap(t => tokenizeSentences(t));
  const phraseFreq = {};
  texts.flatMap(t => nGrams(tokenize(t, false), 3)).forEach(([g, c]) => { if (c > 1) phraseFreq[g] = (phraseFreq[g] || 0) + c; });
  const repeatedPhrases = Object.entries(phraseFreq).sort((a, b) => b[1] - a[1]).slice(0, 15).map(([phrase, count]) => ({ phrase, count }));
  const nearDuplicates = [];
  for (let i = 0; i < Math.min(allSents.length, 40); i++)
    for (let j = i + 1; j < Math.min(allSents.length, 40); j++) {
      const s1 = new Set(tokenize(allSents[i], true)), s2 = new Set(tokenize(allSents[j], true));
      const inter = [...s1].filter(w => s2.has(w)).length;
      const sim = inter / Math.max(s1.size + s2.size - inter, 1);
      if (sim > 0.6 && sim < 1) nearDuplicates.push({ i, j, sim: +sim.toFixed(2), s1: allSents[i], s2: allSents[j] });
    }
  return { repeatedPhrases, nearDuplicates: nearDuplicates.slice(0, 5), redundancyScore: +(nearDuplicates.length / Math.max(allSents.length, 1) * 100).toFixed(1) };
}

// ─────────────────────────────────────────────────────────────────────────────
// SHARED UI ATOMS  (self-contained, no external Panel/Metric deps)
// ─────────────────────────────────────────────────────────────────────────────
const Card = ({ children, style = {} }) => (
  <div style={{ background: C.surface, border: `1px solid ${C.b1}`, borderRadius: 4, overflow: 'hidden', ...style }}>{children}</div>
);
const Head = ({ label, accent = C.pink, right, sub }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '9px 14px', background: C.deep, borderBottom: `1px solid ${C.b0}` }}>
    <div>
      <div style={{ fontSize: 9, color: accent, letterSpacing: '.13em', fontWeight: 700, fontFamily: 'JetBrains Mono,monospace' }}>{label}</div>
      {sub && <div style={{ fontSize: 8, color: C.t4, marginTop: 1 }}>{sub}</div>}
    </div>
    {right}
  </div>
);
const Body = ({ children, style = {} }) => <div style={{ padding: 14, ...style }}>{children}</div>;

const MB = ({ label, value, color = C.pink, sub }) => (
  <div style={{ background: C.raised, border: `1px solid ${color}22`, borderLeft: `2px solid ${color}`, padding: '9px 13px', borderRadius: 3 }}>
    <div style={{ fontSize: 8, color: C.t4, fontFamily: 'JetBrains Mono,monospace', letterSpacing: '.1em' }}>{label}</div>
    <div style={{ fontFamily: 'Syne,sans-serif', fontWeight: 800, fontSize: 18, color, lineHeight: 1.1 }}>{value}</div>
    {sub && <div style={{ fontSize: 9, color: C.t3, marginTop: 2 }}>{sub}</div>}
  </div>
);

const TH = { padding: '6px 11px', textAlign: 'left', fontSize: 9, color: C.t3, letterSpacing: '.09em', borderBottom: `1px solid ${C.b0}`, fontFamily: 'JetBrains Mono,monospace', whiteSpace: 'nowrap' };
const TD = { padding: '6px 11px', color: '#b6c3d8', fontFamily: 'JetBrains Mono,monospace', fontSize: 10 };

const MiniBar = ({ pct, color = C.pink, height = 4 }) => (
  <div style={{ width: '100%', height, background: C.b1, borderRadius: 2 }}>
    <div style={{ height: '100%', width: `${Math.min(pct, 100)}%`, background: color, borderRadius: 2 }} />
  </div>
);

const InfoBox = ({ children, color = C.cyan }) => (
  <div style={{ padding: '10px 13px', background: color + '10', border: `1px solid ${color}33`, borderRadius: 3, fontSize: 11, color: C.t2, lineHeight: 1.7, marginTop: 8 }}>
    {children}
  </div>
);

const sentColor = s => s === 'positive' ? C.green : s === 'negative' ? C.red : C.t3;

// Word Cloud — pure CSS, sized by weight
const WordCloud = ({ words, maxWords = 60, title, accent = C.pink }) => {
  if (!words || !words.length) return null;
  const top = words.slice(0, maxWords);
  const getVal = w => w.count || w.tfidf || w.score || 1;
  const max = Math.max(...top.map(getVal), 1);
  return (
    <Card>
      <Head label={title || 'WORD CLOUD'} accent={accent} />
      <Body>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px 12px', padding: '6px 0', alignItems: 'center', lineHeight: 2 }}>
          {top.map((w, i) => {
            const pct = getVal(w) / max;
            const size = Math.round(10 + pct * 20);
            return (
              <span key={w.word || w} style={{
                fontSize: `${size}px`, color: PALETTE[i % PALETTE.length],
                opacity: 0.5 + pct * 0.5, fontWeight: pct > 0.6 ? 800 : pct > 0.3 ? 600 : 400,
                cursor: 'default', userSelect: 'none',
              }} title={`${w.word}: ${typeof getVal(w) === 'number' ? getVal(w).toFixed ? getVal(w).toFixed(3) : getVal(w) : getVal(w)}`}>
                {w.word || w}
              </span>
            );
          })}
        </div>
      </Body>
    </Card>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// SETUP TAB
// ─────────────────────────────────────────────────────────────────────────────
function SetupTab({ ds, config, onConfigChange }) {
  const types = useMemo(() => inferTypes(ds.headers, ds.rows), [ds]);
  const textCols = useMemo(() => ds.headers.filter(h => {
    const t = types.find(t => t.col === h);
    if (t?.type !== 'categorical') return false;
    const vals = ds.rows.map(r => String(r[h] || '')).filter(Boolean);
    const avgLen = vals.reduce((s, v) => s + v.length, 0) / Math.max(vals.length, 1);
    return avgLen > 15 || t.uniqueCount / ds.rows.length > 0.25;
  }), [ds, types]);

  const profiles = useMemo(() => ds.headers.map(h => {
    const t = types.find(t => t.col === h);
    const vals = ds.rows.map(r => String(r[h] || '')).filter(Boolean);
    const avgLen = +(vals.reduce((s, v) => s + v.length, 0) / Math.max(vals.length, 1)).toFixed(0);
    const avgWords = +(vals.reduce((s, v) => s + v.split(/\s+/).length, 0) / Math.max(vals.length, 1)).toFixed(1);
    return { h, type: t?.type, avgLen, avgWords, unique: t?.uniqueCount, uniquePct: +(t?.uniqueCount / ds.rows.length * 100).toFixed(1), nulls: t?.nullCount, isText: textCols.includes(h) };
  }), [ds, types, textCols]);

  const { primaryCol = '', secondaryCol = '', useStopwords = true, sampleSize = 500 } = config;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <Card>
        <Head label="COLUMN PROFILER — AUTO-DETECTED TEXT COLUMNS" accent={C.pink} sub={`${textCols.length} text-like columns detected`} />
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
            <thead><tr style={{ background: C.deep }}>
              {['COLUMN', 'TYPE', 'AVG CHARS', 'AVG WORDS', 'UNIQUE', 'UNIQUE %', 'NULLS', 'TEXT?', 'SET AS'].map(h => <th key={h} style={TH}>{h}</th>)}
            </tr></thead>
            <tbody>
              {profiles.map(p => (
                <tr key={p.h} style={{ borderBottom: `1px solid ${C.b0}`, background: p.isText ? C.pink + '08' : 'transparent' }}>
                  <td style={{ ...TD, color: p.isText ? C.pink : C.t2, fontWeight: p.isText ? 700 : 400, borderLeft: `2px solid ${p.isText ? C.pink : C.b1}` }}>{p.h}</td>
                  <td style={{ ...TD }}><span style={{ fontSize: 9, padding: '1px 6px', borderRadius: 2, background: (p.type === 'numeric' ? C.cyan : C.gold) + '18', color: p.type === 'numeric' ? C.cyan : C.gold }}>{p.type}</span></td>
                  <td style={{ ...TD, color: C.t2 }}>{p.avgLen}</td>
                  <td style={{ ...TD, color: C.t2 }}>{p.avgWords}</td>
                  <td style={{ ...TD }}>{p.unique}</td>
                  <td style={{ ...TD, color: +p.uniquePct > 50 ? C.green : C.t3 }}>{p.uniquePct}%</td>
                  <td style={{ ...TD, color: p.nulls > 0 ? C.red : C.t4 }}>{p.nulls}</td>
                  <td style={{ ...TD, color: p.isText ? C.green : C.t4, fontWeight: 700 }}>{p.isText ? '✓ TEXT' : '—'}</td>
                  <td style={{ ...TD }}>
                    <div style={{ display: 'flex', gap: 4 }}>
                      <button onClick={() => onConfigChange({ primaryCol: p.h })} style={{ padding: '2px 8px', border: `1px solid ${primaryCol === p.h ? C.pink : C.b1}`, background: primaryCol === p.h ? C.pink + '20' : 'transparent', color: primaryCol === p.h ? C.pink : C.t4, fontSize: 8, borderRadius: 2, cursor: 'pointer', fontFamily: 'JetBrains Mono,monospace', fontWeight: 700 }}>PRIMARY</button>
                      <button onClick={() => onConfigChange({ secondaryCol: secondaryCol === p.h ? '' : p.h })} style={{ padding: '2px 8px', border: `1px solid ${secondaryCol === p.h ? C.violet : C.b1}`, background: secondaryCol === p.h ? C.violet + '20' : 'transparent', color: secondaryCol === p.h ? C.violet : C.t4, fontSize: 8, borderRadius: 2, cursor: 'pointer', fontFamily: 'JetBrains Mono,monospace' }}>+GROUP</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
        <Card>
          <Head label="ANALYSIS CONFIGURATION" accent={C.pink} />
          <Body>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div>
                <Label>PRIMARY TEXT COLUMN</Label>
                <select value={primaryCol} onChange={e => onConfigChange({ primaryCol: e.target.value })}
                  style={{ width: '100%', background: C.deep, border: `2px solid ${C.pink}`, color: C.t1, padding: '8px 12px', fontSize: 12, fontFamily: 'JetBrains Mono,monospace', borderRadius: 3, cursor: 'pointer', outline: 'none' }}>
                  {ds.headers.map(h => <option key={h} value={h}>{h}</option>)}
                </select>
              </div>
              <div>
                <Label>GROUP BY COLUMN (optional — for sentiment by group)</Label>
                <select value={secondaryCol} onChange={e => onConfigChange({ secondaryCol: e.target.value })}
                  style={{ width: '100%', background: C.deep, border: `1px solid ${C.b2}`, color: C.t1, padding: '7px 10px', fontSize: 11, fontFamily: 'JetBrains Mono,monospace', borderRadius: 3, cursor: 'pointer', outline: 'none' }}>
                  <option value="">— none —</option>
                  {ds.headers.filter(h => h !== primaryCol).map(h => <option key={h} value={h}>{h}</option>)}
                </select>
              </div>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                <input type="checkbox" checked={useStopwords} onChange={e => onConfigChange({ useStopwords: e.target.checked })} style={{ accentColor: C.pink, width: 14, height: 14 }} />
                <span style={{ fontSize: 11, color: C.t2 }}>Remove stopwords (the, and, is…)</span>
              </label>
              <div>
                <Label>SAMPLE SIZE</Label>
                <div style={{ display: 'flex', gap: 6 }}>
                  {[200, 500, 1000, 2000, 'ALL'].map(n => (
                    <button key={n} onClick={() => onConfigChange({ sampleSize: n === 'ALL' ? 999999 : n })}
                      style={{ padding: '5px 10px', border: `1px solid ${sampleSize === (n === 'ALL' ? 999999 : n) ? C.pink : C.b1}`, background: sampleSize === (n === 'ALL' ? 999999 : n) ? C.pink + '18' : 'transparent', color: sampleSize === (n === 'ALL' ? 999999 : n) ? C.pink : C.t3, fontSize: 9, borderRadius: 3, cursor: 'pointer', fontFamily: 'JetBrains Mono,monospace' }}>{n}</button>
                  ))}
                </div>
              </div>
            </div>
          </Body>
        </Card>
        <Card>
          <Head label="LIVE PREVIEW — SELECTED COLUMN" accent={C.violet} />
          <Body>
            {primaryCol ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {ds.rows.slice(0, 6).map((r, i) => (
                  <div key={i} style={{ padding: '8px 10px', background: C.raised, borderRadius: 3, borderLeft: `2px solid ${PALETTE[i % PALETTE.length]}` }}>
                    <div style={{ fontSize: 8, color: PALETTE[i % PALETTE.length], fontFamily: 'JetBrains Mono,monospace', marginBottom: 2 }}>ROW {i + 1}</div>
                    <div style={{ fontSize: 11, color: C.t2, lineHeight: 1.6, wordBreak: 'break-word' }}>{String(r[primaryCol] || '').slice(0, 160)}{String(r[primaryCol] || '').length > 160 && '…'}</div>
                  </div>
                ))}
              </div>
            ) : <div style={{ color: C.t4, fontSize: 12 }}>Select a primary column →</div>}
          </Body>
        </Card>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// STATS TAB
// ─────────────────────────────────────────────────────────────────────────────
function StatsTab({ texts, col }) {
  const stats  = useMemo(() => texts.length ? getBasicStats(texts) : null,           [texts]);
  const wld    = useMemo(() => texts.length ? getWordLengthDist(texts) : [],          [texts]);
  const sld    = useMemo(() => texts.length ? getSentLengthDist(texts) : [],          [texts]);
  const ch     = useMemo(() => texts.length ? getCharAnalysis(texts) : null,          [texts]);
  const div    = useMemo(() => texts.length ? getLinguisticDiversity(texts) : null,   [texts]);
  const lang   = useMemo(() => texts.length ? detectLang(texts.slice(0, 20).join(' ')) : '', [texts]);

  if (!stats) return <div style={{ padding: 40, textAlign: 'center', color: C.t4 }}>No data. Configure a column in SETUP first.</div>;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {/* Core counts */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10 }}>
        <MB label="WORDS"           value={stats.words.toLocaleString()}  color={C.cyan} />
        <MB label="UNIQUE WORDS"    value={stats.unique.toLocaleString()} color={C.green}   sub={`${(stats.lexDiv * 100).toFixed(1)}% diversity`} />
        <MB label="SENTENCES"       value={stats.sentences}               color={C.violet}  sub={`avg ${stats.avgSentLen} words/sent`} />
        <MB label="DOCUMENTS"       value={stats.paragraphs}              color={C.gold} />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10 }}>
        <MB label="CHARACTERS"      value={stats.chars.toLocaleString()}  color={C.t2} />
        <MB label="AVG WORD LENGTH" value={`${stats.avgWordLen} chars`}   color={C.cyan} />
        <MB label="EST. READ TIME"  value={`~${stats.readTime} min`}      color={C.green}   sub="at 200 wpm" />
        <MB label="LANGUAGE"        value={lang}                          color={C.violet} />
      </div>

      {/* Distributions */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 14 }}>
        <Card>
          <Head label="WORD LENGTH DISTRIBUTION" accent={C.cyan} right={<PinButton chart={{ id: 'nlp_wld', type: 'bar', title: 'Word Length Distribution' }} small />} />
          <Body>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={wld.filter(d => d.len <= 15)} barCategoryGap="5%">
                <CartesianGrid strokeDasharray="2 2" stroke={C.b0} />
                <XAxis dataKey="len" tick={{ fill: C.t4, fontSize: 9 }} label={{ value: 'chars', position: 'insideBottom', offset: -2, fill: C.t3, fontSize: 8 }} />
                <YAxis tick={{ fill: C.t4, fontSize: 8 }} />
                <Tooltip content={<ChartTip />} />
                <Bar dataKey="count" name="words" radius={[2, 2, 0, 0]}>
                  {wld.map((_, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} opacity={0.85} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </Body>
        </Card>
        <Card>
          <Head label="SENTENCE LENGTH TREND" accent={C.violet} right={<PinButton chart={{ id: 'nlp_sld', type: 'area', title: 'Sentence Length' }} small />} />
          <Body>
            <ResponsiveContainer width="100%" height={200}>
              <AreaChart data={sld.slice(0, 60)}>
                <CartesianGrid strokeDasharray="2 2" stroke={C.b0} />
                <XAxis dataKey="idx" tick={{ fill: C.t4, fontSize: 8 }} label={{ value: 'sentence #', position: 'insideBottom', offset: -2, fill: C.t3, fontSize: 8 }} />
                <YAxis tick={{ fill: C.t4, fontSize: 8 }} />
                <Tooltip content={<ChartTip />} />
                <Area type="monotone" dataKey="words" stroke={C.violet} fill={C.violet} fillOpacity={0.18} name="words" />
              </AreaChart>
            </ResponsiveContainer>
          </Body>
        </Card>
        <Card>
          <Head label="LETTER FREQUENCY" accent={C.green} right={<PinButton chart={{ id: 'nlp_lf', type: 'bar', title: 'Letter Frequency' }} small />} />
          <Body>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={(ch?.charFreq || []).slice(0, 20)} barCategoryGap="3%">
                <CartesianGrid strokeDasharray="2 2" stroke={C.b0} />
                <XAxis dataKey="char" tick={{ fill: C.t4, fontSize: 9 }} />
                <YAxis tick={{ fill: C.t4, fontSize: 8 }} />
                <Tooltip content={<ChartTip />} />
                <Bar dataKey="count" fill={C.green} opacity={0.8} radius={[2, 2, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Body>
        </Card>
      </div>

      {/* Char composition + diversity */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
        <Card>
          <Head label="CHARACTER COMPOSITION" accent={C.green} />
          <Body>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              {[['LOWERCASE', ch?.lowercase, C.cyan], ['UPPERCASE', ch?.uppercase, C.gold], ['DIGITS', ch?.digits, C.green], ['PUNCTUATION', ch?.punctuation, C.violet], ['VOWELS', ch?.vowels, C.red], ['CONSONANTS', ch?.consonants, C.orange], ['VOWEL RATIO', `${ch?.vowelRatio}%`, C.cyan], ['SHANNON ENTROPY', ch?.entropy, C.pink]].map(([l, v, c]) => (
                <MB key={l} label={l} value={typeof v === 'number' ? v.toLocaleString() : (v || '—')} color={c} />
              ))}
            </div>
            <InfoBox color={C.cyan}>Vowel ratio: {ch?.vowelRatio}% — English prose typically 38–42%.</InfoBox>
          </Body>
        </Card>
        <Card>
          <Head label="LEXICAL DIVERSITY" accent={C.violet} />
          <Body>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 12 }}>
              <MB label="TYPE-TOKEN RATIO"  value={div?.ttr}                                      color={C.violet} sub="unique ÷ total" />
              <MB label="ROOT TTR"          value={div?.rttr}                                     color={C.cyan} />
              <MB label="CORRECTED TTR"     value={div?.cttr}                                     color={C.green} />
              <MB label="HERDAN'S C"        value={div?.herdanC}                                  color={C.gold} />
              <MB label="YULE'S K"          value={div?.yulesK}                                   color={C.orange} sub="vocab richness" />
              <MB label="HAPAX LEGOMENA"    value={div?.hapax}                                    color={C.pink}   sub={`${div?.hapaxRatio ? (div.hapaxRatio * 100).toFixed(0) : 0}% of vocab`} />
            </div>
            {div?.ttrCurve?.length > 0 && (
              <>
                <div style={{ fontSize: 9, color: C.t3, fontFamily: 'JetBrains Mono,monospace', marginBottom: 6 }}>VOCABULARY GROWTH CURVE — TTR drops faster with repetition</div>
                <ResponsiveContainer width="100%" height={120}>
                  <LineChart data={div.ttrCurve}>
                    <CartesianGrid strokeDasharray="2 2" stroke={C.b0} />
                    <XAxis dataKey="pos" tick={{ fill: C.t4, fontSize: 8 }} />
                    <YAxis domain={[0, 1]} tick={{ fill: C.t4, fontSize: 8 }} />
                    <Tooltip content={<ChartTip />} />
                    <Line type="monotone" dataKey="ttr" stroke={C.cyan} strokeWidth={2} dot={false} name="TTR" />
                  </LineChart>
                </ResponsiveContainer>
              </>
            )}
          </Body>
        </Card>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// FREQUENCY TAB
// ─────────────────────────────────────────────────────────────────────────────
function FrequencyTab({ texts, col, config }) {
  const [view, setView]               = useState('words');
  const [showStopwords, setShowStop]  = useState(false);
  const [freqN, setFreqN]             = useState(30);

  const freq = useMemo(() => {
    const f = {};
    texts.forEach(t => tokenize(t, config.useStopwords).forEach(w => { f[w] = (f[w] || 0) + 1; }));
    return Object.entries(f).sort((a, b) => b[1] - a[1]).map(([word, count]) => ({ word, count }));
  }, [texts, config]);

  const freqAll = useMemo(() => {
    const f = {};
    texts.forEach(t => tokenize(t, false).forEach(w => { f[w] = (f[w] || 0) + 1; }));
    return Object.entries(f).sort((a, b) => b[1] - a[1]).map(([word, count]) => ({ word, count }));
  }, [texts]);

  const bigrams  = useMemo(() => nGrams(texts.flatMap(t => tokenize(t, false)), 2).slice(0, 40).map(([g, c]) => ({ bigram: g, count: c })),  [texts]);
  const trigrams = useMemo(() => nGrams(texts.flatMap(t => tokenize(t, false)), 3).slice(0, 25).map(([g, c]) => ({ trigram: g, count: c })), [texts]);
  const chars    = useMemo(() => getCharAnalysis(texts), [texts]);

  const display = showStopwords ? freqAll.slice(0, freqN) : freq.slice(0, freqN);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {/* Controls */}
      <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', gap: 2, background: C.deep, border: `1px solid ${C.b0}`, borderRadius: 3, padding: 2 }}>
          {[['words', 'WORDS'], ['bigrams', 'BIGRAMS'], ['trigrams', 'TRIGRAMS'], ['chars', 'CHARS']].map(([v, l]) => (
            <button key={v} onClick={() => setView(v)} style={{ padding: '6px 13px', border: 'none', cursor: 'pointer', fontSize: 10, letterSpacing: '.08em', fontFamily: 'JetBrains Mono,monospace', background: view === v ? C.green : 'transparent', color: view === v ? C.void : C.t2, fontWeight: view === v ? 700 : 400, borderRadius: 2, transition: 'all .12s' }}>{l}</button>
          ))}
        </div>
        {view === 'words' && (
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 11, color: C.t2, cursor: 'pointer' }}>
            <input type="checkbox" checked={showStopwords} onChange={e => setShowStop(e.target.checked)} style={{ accentColor: C.green }} />
            Show stopwords
          </label>
        )}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginLeft: 'auto' }}>
          <span style={{ fontSize: 11, color: C.t3 }}>Top {freqN}:</span>
          <input type="range" min={10} max={50} step={5} value={freqN} onChange={e => setFreqN(+e.target.value)} style={{ width: 100, accentColor: C.green }} />
        </div>
      </div>

      {view === 'words' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <WordCloud words={display} title={`WORD CLOUD — ${col}${showStopwords ? ' (with stopwords)' : ''}`} accent={C.pink} />
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 14 }}>
            <Card>
              <Head label={`WORD FREQUENCY — TOP ${freqN}`} accent={C.green} right={<PinButton chart={{ id: 'nlp_freq', type: 'bar', title: 'Word Frequency', subtitle: col }} small />} />
              <Body>
                <ResponsiveContainer width="100%" height={Math.max(300, freqN * 16)}>
                  <BarChart data={display} layout="vertical" barCategoryGap="8%" margin={{ top: 0, right: 70, bottom: 0, left: 0 }}>
                    <CartesianGrid strokeDasharray="2 2" stroke={C.b0} horizontal={false} />
                    <XAxis type="number" tick={{ fill: C.t4, fontSize: 9 }} />
                    <YAxis dataKey="word" type="category" tick={{ fill: C.t2, fontSize: 9 }} width={100} />
                    <Tooltip content={<ChartTip />} />
                    <Bar dataKey="count" name="count" radius={[0, 3, 3, 0]}>
                      {display.map((_, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </Body>
            </Card>
            <Card>
              <Head label="ZIPF'S LAW" accent={C.green} right={<PinButton chart={{ id: 'nlp_zipf', type: 'scatter', title: "Zipf's Law" }} small />} />
              <Body>
                <div style={{ fontSize: 11, color: C.t3, marginBottom: 10 }}>Rank vs frequency — natural language follows a power law.</div>
                <ResponsiveContainer width="100%" height={220}>
                  <ScatterChart>
                    <CartesianGrid strokeDasharray="2 2" stroke={C.b0} />
                    <XAxis dataKey="rank" tick={{ fill: C.t4, fontSize: 9 }} name="Rank" label={{ value: 'rank', position: 'insideBottom', offset: -2, fill: C.t3, fontSize: 9 }} />
                    <YAxis dataKey="count" tick={{ fill: C.t4, fontSize: 9 }} name="Freq" />
                    <Tooltip cursor={{ strokeDasharray: '3 3' }} content={<ChartTip />} />
                    <Scatter data={display.map((d, i) => ({ rank: i + 1, count: d.count }))} fill={C.green} opacity={0.7} />
                  </ScatterChart>
                </ResponsiveContainer>
                <InfoBox color={C.green}>Zipf's Law: rank × frequency ≈ constant in natural language.</InfoBox>
              </Body>
            </Card>
          </div>
        </div>
      )}

      {view === 'bigrams' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <WordCloud words={bigrams.slice(0, 40).map(g => ({ word: g.bigram, count: g.count }))} title="BIGRAM CLOUD" accent={C.cyan} />
          <Card>
            <Head label={`BIGRAMS (2-word phrases) — ${bigrams.length} found`} accent={C.cyan} right={<PinButton chart={{ id: 'nlp_bigrams', type: 'bar', title: 'Bigrams' }} small />} />
            <Body>
              <ResponsiveContainer width="100%" height={Math.max(300, Math.min(freqN, bigrams.length) * 18)}>
                <BarChart data={bigrams.slice(0, freqN)} layout="vertical" barCategoryGap="8%" margin={{ top: 0, right: 70, bottom: 0, left: 0 }}>
                  <CartesianGrid strokeDasharray="2 2" stroke={C.b0} horizontal={false} />
                  <XAxis type="number" tick={{ fill: C.t4, fontSize: 9 }} />
                  <YAxis dataKey="bigram" type="category" tick={{ fill: C.t2, fontSize: 9 }} width={160} />
                  <Tooltip content={<ChartTip />} />
                  <Bar dataKey="count" fill={C.cyan} opacity={0.85} radius={[0, 3, 3, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </Body>
          </Card>
        </div>
      )}

      {view === 'trigrams' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <WordCloud words={trigrams.slice(0, 25).map(g => ({ word: g.trigram, count: g.count }))} title="TRIGRAM CLOUD" accent={C.violet} />
          <Card>
            <Head label={`TRIGRAMS (3-word phrases) — ${trigrams.length} found`} accent={C.violet} right={<PinButton chart={{ id: 'nlp_trigrams', type: 'bar', title: 'Trigrams' }} small />} />
            <Body>
              <ResponsiveContainer width="100%" height={Math.max(280, Math.min(freqN, trigrams.length) * 20)}>
                <BarChart data={trigrams.slice(0, freqN)} layout="vertical" barCategoryGap="8%" margin={{ top: 0, right: 70, bottom: 0, left: 0 }}>
                  <CartesianGrid strokeDasharray="2 2" stroke={C.b0} horizontal={false} />
                  <XAxis type="number" tick={{ fill: C.t4, fontSize: 9 }} />
                  <YAxis dataKey="trigram" type="category" tick={{ fill: C.t2, fontSize: 9 }} width={220} />
                  <Tooltip content={<ChartTip />} />
                  <Bar dataKey="count" fill={C.violet} opacity={0.85} radius={[0, 3, 3, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </Body>
          </Card>
        </div>
      )}

      {view === 'chars' && chars && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
          <Card>
            <Head label="LETTER FREQUENCY A–Z" accent={C.green} right={<PinButton chart={{ id: 'nlp_letters', type: 'bar', title: 'Letter Frequency' }} small />} />
            <Body>
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={chars.charFreq} barCategoryGap="3%">
                  <CartesianGrid strokeDasharray="2 2" stroke={C.b0} />
                  <XAxis dataKey="char" tick={{ fill: C.t4, fontSize: 9 }} />
                  <YAxis tick={{ fill: C.t4, fontSize: 8 }} />
                  <Tooltip content={<ChartTip />} />
                  <Bar dataKey="count" fill={C.green} opacity={0.85} radius={[2, 2, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </Body>
          </Card>
          <Card>
            <Head label="CHARACTER COMPOSITION" accent={C.green} />
            <Body>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                {[['LOWERCASE', chars.lowercase, C.cyan], ['UPPERCASE', chars.uppercase, C.gold], ['DIGITS', chars.digits, C.green], ['PUNCTUATION', chars.punctuation, C.violet], ['VOWELS', chars.vowels, C.red], ['CONSONANTS', chars.consonants, C.orange], ['VOWEL RATIO', `${chars.vowelRatio}%`, C.cyan], ['ENTROPY', chars.entropy, C.pink]].map(([l, v, c]) => (
                  <MB key={l} label={l} value={typeof v === 'number' ? v.toLocaleString() : (v || '—')} color={c} />
                ))}
              </div>
              <InfoBox color={C.cyan}>Vowel ratio: {chars.vowelRatio}% — English prose typically 38–42%.</InfoBox>
            </Body>
          </Card>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// READABILITY TAB
// ─────────────────────────────────────────────────────────────────────────────
function ReadabilityTab({ texts }) {
  const rd    = useMemo(() => texts.length ? fleschAll(texts) : null,          [texts]);
  const stats = useMemo(() => texts.length ? getBasicStats(texts) : null,       [texts]);
  if (!rd) return <div style={{ padding: 40, textAlign: 'center', color: C.t4 }}>No data.</div>;

  const radarData = [
    { metric: 'Flesch Ease',    score: Math.min(100, rd.flesch) },
    { metric: 'FK Grade',       score: Math.min(20, rd.fkGrade) * 5 },
    { metric: 'Fog Index',      score: Math.min(20, rd.fog) * 5 },
    { metric: 'Lex Diversity',  score: (stats?.lexDiv || 0) * 100 },
    { metric: 'Complex Words%', score: rd.complexPct },
    { metric: 'Avg Sent Len',   score: Math.min(40, stats?.avgSentLen || 0) * 2.5 },
  ];
  const gradeData = [
    { index: 'FK Grade',    value: rd.fkGrade, color: C.cyan },
    { index: 'Fog Index',   value: rd.fog,     color: C.violet },
    { index: 'SMOG',        value: rd.smog,    color: C.gold },
    { index: 'ARI',         value: rd.ari,     color: C.orange },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10 }}>
        <MB label="FLESCH READING EASE" value={rd.flesch}   color={rd.flesch > 60 ? C.green : rd.flesch > 40 ? C.gold : C.red} sub={rd.grade} />
        <MB label="FK GRADE LEVEL"      value={rd.fkGrade}  color={C.cyan} />
        <MB label="GUNNING FOG"         value={rd.fog}      color={C.violet} />
        <MB label="SMOG INDEX"          value={rd.smog}     color={C.gold}   sub="~grade level" />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10 }}>
        <MB label="ARI"               value={rd.ari}           color={C.orange} sub="Auto Readability" />
        <MB label="AVG WORDS/SENTENCE" value={rd.awps}         color={C.t2} />
        <MB label="AVG SYLLABLES/WORD" value={rd.asw}          color={C.t2} />
        <MB label="COMPLEX WORDS"      value={rd.complexWords} color={C.red}    sub={`${rd.complexPct}% of total`} />
      </div>
      {/* Gauge */}
      <Card>
        <Head label="READABILITY GAUGE" accent={C.cyan} />
        <Body>
          <div style={{ display: 'flex', gap: 20, alignItems: 'center', marginBottom: 14 }}>
            <div style={{ fontFamily: 'Syne,sans-serif', fontWeight: 800, fontSize: 52, color: rd.flesch > 60 ? C.green : rd.flesch > 40 ? C.gold : C.red, lineHeight: 1 }}>{rd.flesch}</div>
            <div>
              <div style={{ fontFamily: 'Syne,sans-serif', fontWeight: 700, fontSize: 20, color: C.t1 }}>{rd.grade}</div>
              <div style={{ fontSize: 10, color: C.t3, marginTop: 3 }}>Flesch Reading Ease · 0 = Very Hard · 100 = Very Easy</div>
            </div>
          </div>
          <div style={{ height: 10, borderRadius: 5, background: `linear-gradient(90deg,${C.red} 0%,${C.gold} 40%,${C.green} 70%,${C.cyan} 100%)`, position: 'relative', marginBottom: 6 }}>
            <div style={{ position: 'absolute', top: -3, left: `${Math.min(Math.max(rd.flesch, 0), 100)}%`, transform: 'translateX(-50%)', width: 16, height: 16, borderRadius: '50%', background: C.t1, border: `3px solid ${C.void}`, boxShadow: `0 0 0 2px ${rd.flesch > 60 ? C.green : rd.flesch > 40 ? C.gold : C.red}` }} />
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 8, color: C.t4 }}>
            <span>0 Very Hard</span><span>30</span><span>60 Standard</span><span>80</span><span>100 Very Easy</span>
          </div>
          <InfoBox color={C.cyan}>Academic papers ≈ 20–40 · News articles ≈ 60–70 · Children's books ≈ 80–90. Grade-level metrics estimate years of education required.</InfoBox>
        </Body>
      </Card>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
        <Card>
          <Head label="READABILITY RADAR" accent={C.violet} right={<PinButton chart={{ id: 'nlp_rdradar', type: 'radar', title: 'Readability Radar' }} small />} />
          <Body>
            <ResponsiveContainer width="100%" height={280}>
              <RadarChart data={radarData}>
                <PolarGrid stroke={C.b1} />
                <PolarAngleAxis dataKey="metric" tick={{ fill: C.t3, fontSize: 10 }} />
                <PolarRadiusAxis angle={30} domain={[0, 100]} tick={{ fill: C.t4, fontSize: 8 }} />
                <Radar dataKey="score" stroke={C.violet} fill={C.violet} fillOpacity={0.2} />
                <Tooltip content={<ChartTip />} />
              </RadarChart>
            </ResponsiveContainer>
          </Body>
        </Card>
        <Card>
          <Head label="GRADE LEVEL COMPARISON" accent={C.violet} right={<PinButton chart={{ id: 'nlp_rdgrade', type: 'bar', title: 'Grade Level Comparison' }} small />} />
          <Body>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={gradeData} barCategoryGap="20%">
                <CartesianGrid strokeDasharray="2 2" stroke={C.b0} />
                <XAxis dataKey="index" tick={{ fill: C.t3, fontSize: 10 }} />
                <YAxis tick={{ fill: C.t4, fontSize: 8 }} label={{ value: 'grade', angle: -90, position: 'insideLeft', fill: C.t3, fontSize: 9 }} />
                <Tooltip content={<ChartTip />} />
                <Bar dataKey="value" name="grade" radius={[3, 3, 0, 0]}>
                  {gradeData.map((d, i) => <Cell key={i} fill={d.color} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginTop: 10 }}>
              <MB label="TOTAL SYLLABLES" value={rd.syl.toLocaleString()} color={C.t2} />
              <MB label="COMPLEX WORDS %" value={`${rd.complexPct}%`}      color={C.red} />
            </div>
          </Body>
        </Card>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// SENTIMENT TAB
// ─────────────────────────────────────────────────────────────────────────────
function SentimentTab({ texts, rows, col, config }) {
  const [sentView, setSentView] = useState('overview');
  const [filter,   setFilter]   = useState('all');
  const [search,   setSearch]   = useState('');

  const scored     = useMemo(() => texts.map((t, i) => ({ idx: i, text: t, ...sentimentScore(t) })),                                         [texts]);
  const sentScores = useMemo(() => texts.flatMap(t => tokenizeSentences(t)).map(s => ({ text: s, ...sentimentScore(s) })),                    [texts]);

  const summary = useMemo(() => {
    const pos = scored.filter(s => s.label === 'positive');
    const neg = scored.filter(s => s.label === 'negative');
    const avg = +(scored.reduce((a, s) => a + s.score, 0) / scored.length).toFixed(4);
    return {
      pos: pos.length, neg: neg.length, neu: scored.length - pos.length - neg.length, avg,
      pct: { pos: +(pos.length / scored.length * 100).toFixed(1), neg: +(neg.length / scored.length * 100).toFixed(1) },
      posSents: sentScores.filter(s => s.label === 'positive').length,
      negSents: sentScores.filter(s => s.label === 'negative').length,
    };
  }, [scored, sentScores]);

  const scoreBins = useMemo(() => {
    const bins = Array.from({ length: 20 }, (_, i) => ({ range: `${(-1 + i * 0.1).toFixed(1)}`, count: 0 }));
    scored.forEach(s => { const idx = Math.min(19, Math.floor((s.score + 1) / 0.1)); bins[idx].count++; });
    return bins;
  }, [scored]);

  const trend = useMemo(() => {
    const win = Math.max(1, Math.floor(sentScores.length / 40)); const out = [];
    for (let i = 0; i < sentScores.length; i += win) {
      const chunk = sentScores.slice(i, i + win);
      out.push({ idx: i + 1, avg: +(chunk.reduce((a, s) => a + s.score, 0) / chunk.length).toFixed(4) });
    }
    return out;
  }, [sentScores]);

  const groupStats = useMemo(() => {
    if (!config.secondaryCol) return [];
    const groups = {};
    rows.slice(0, texts.length).forEach((r, i) => {
      const g = r[config.secondaryCol] || '—';
      if (!groups[g]) groups[g] = { label: g, scores: [] };
      groups[g].scores.push(scored[i]?.score || 0);
    });
    return Object.values(groups).map(g => ({ ...g, avg: +(g.scores.reduce((a, b) => a + b, 0) / g.scores.length).toFixed(3) })).sort((a, b) => b.avg - a.avg);
  }, [rows, texts, scored, config]);

  const filtered = useMemo(() => {
    let s = filter !== 'all' ? scored.filter(x => x.label === filter) : scored;
    if (search.trim()) s = s.filter(x => x.text.toLowerCase().includes(search.toLowerCase()));
    return s;
  }, [scored, filter, search]);

  const overallColor = summary.avg > 0.15 ? C.green : summary.avg < -0.15 ? C.red : C.t3;
  const overallLabel = summary.avg > 0.15 ? '😊 POSITIVE' : summary.avg < -0.15 ? '😞 NEGATIVE' : '😐 NEUTRAL';
  const pieData = [{ name: 'Positive', value: summary.pos, color: C.green }, { name: 'Neutral', value: summary.neu, color: C.t3 }, { name: 'Negative', value: summary.neg, color: C.red }];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
        {[['overview', 'OVERVIEW'], ['sentences', 'SENTENCE SCORES'], ['timeline', 'SENTIMENT ARC']].map(([v, l]) => (
          <button key={v} onClick={() => setSentView(v)} style={{ padding: '8px 16px', border: `1px solid ${sentView === v ? C.cyan : C.b0}`, background: sentView === v ? C.cyan + '15' : C.raised, color: sentView === v ? C.cyan : C.t2, fontSize: 10, cursor: 'pointer', borderRadius: 3, fontFamily: 'JetBrains Mono,monospace', fontWeight: sentView === v ? 700 : 400, transition: 'all .12s' }}>{l}</button>
        ))}
      </div>

      {sentView === 'overview' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10 }}>
            <MB label="OVERALL SENTIMENT" value={overallLabel} color={overallColor} sub={`score: ${summary.avg}`} />
            <MB label="POSITIVE WORDS"    value={scored.reduce((a, s) => a + s.posCount, 0)} color={C.green} sub={`${summary.pct.pos}% docs positive`} />
            <MB label="NEGATIVE WORDS"    value={scored.reduce((a, s) => a + s.negCount, 0)} color={C.red}   sub={`${summary.pct.neg}% docs negative`} />
            <MB label="POS SENTENCES"     value={summary.posSents} color={C.green} sub={`${summary.negSents} neg`} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            <Card>
              <Head label="SENTIMENT DISTRIBUTION" accent={C.cyan} />
              <Body>
                <div style={{ display: 'flex', height: 32, borderRadius: 4, overflow: 'hidden', marginBottom: 8 }}>
                  {[{ val: summary.posSents, color: C.green, label: 'Positive' }, { val: sentScores.length - summary.posSents - summary.negSents, color: C.t3, label: 'Neutral' }, { val: summary.negSents, color: C.red, label: 'Negative' }].map(({ val, color, label }) => {
                    const pct = sentScores.length > 0 ? val / sentScores.length * 100 : 0;
                    return pct > 0 ? <div key={label} style={{ width: `${pct}%`, background: color, opacity: 0.75, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      {pct > 8 && <span style={{ fontSize: 10, fontWeight: 700, color: C.void, whiteSpace: 'nowrap' }}>{label} {pct.toFixed(0)}%</span>}
                    </div> : null;
                  })}
                </div>
                <div style={{ display: 'flex', justifyContent: 'center', gap: 18, fontSize: 11, marginTop: 6 }}>
                  {[[C.green, 'Positive'], [C.t3, 'Neutral'], [C.red, 'Negative']].map(([c, l]) => (
                    <span key={l} style={{ display: 'flex', alignItems: 'center', gap: 5 }}><div style={{ width: 8, height: 8, background: c, borderRadius: 2 }} />{l}</span>
                  ))}
                </div>
              </Body>
            </Card>
            <Card>
              <Head label="SPLIT PIE" accent={C.green} right={<PinButton chart={{ id: 'nlp_sentpie', type: 'pie', title: 'Sentiment Split' }} small />} />
              <Body>
                <ResponsiveContainer width="100%" height={150}>
                  <PieChart>
                    <Pie data={pieData} dataKey="value" cx="50%" cy="50%" outerRadius={60} paddingAngle={3}
                      label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`} labelLine={false}>
                      {pieData.map((d, i) => <Cell key={i} fill={d.color} />)}
                    </Pie>
                    <Tooltip content={<ChartTip />} />
                  </PieChart>
                </ResponsiveContainer>
              </Body>
            </Card>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            <Card>
              <Head label="SCORE DISTRIBUTION" accent={C.cyan} right={<PinButton chart={{ id: 'nlp_sentdist', type: 'bar', title: 'Sentiment Score Distribution' }} small />} />
              <Body>
                <ResponsiveContainer width="100%" height={180}>
                  <BarChart data={scoreBins} barCategoryGap="2%">
                    <CartesianGrid strokeDasharray="2 2" stroke={C.b0} />
                    <XAxis dataKey="range" tick={{ fill: C.t4, fontSize: 7 }} interval={4} />
                    <YAxis tick={{ fill: C.t4, fontSize: 8 }} />
                    <Tooltip content={<ChartTip />} />
                    <Bar dataKey="count" name="docs" radius={[2, 2, 0, 0]}>
                      {scoreBins.map((b, i) => <Cell key={i} fill={parseFloat(b.range) > 0 ? C.green : parseFloat(b.range) < 0 ? C.red : C.t3} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </Body>
            </Card>
            {groupStats.length > 0 && (
              <Card>
                <Head label={`BY GROUP — ${config.secondaryCol}`} accent={C.gold} right={<PinButton chart={{ id: 'nlp_sentgroup', type: 'bar', title: `Sentiment by ${config.secondaryCol}` }} small />} />
                <Body>
                  <ResponsiveContainer width="100%" height={Math.max(140, groupStats.length * 26 + 30)}>
                    <BarChart data={groupStats} layout="vertical" barCategoryGap="18%" margin={{ top: 0, right: 60, bottom: 0, left: 0 }}>
                      <CartesianGrid strokeDasharray="2 2" stroke={C.b0} horizontal={false} />
                      <XAxis type="number" domain={[-1, 1]} tick={{ fill: C.t4, fontSize: 8 }} />
                      <YAxis dataKey="label" type="category" tick={{ fill: C.t2, fontSize: 9 }} width={110} />
                      <Tooltip content={<ChartTip />} />
                      <Bar dataKey="avg" name="avg sentiment" radius={[0, 3, 3, 0]}>
                        {groupStats.map((g, i) => <Cell key={i} fill={g.avg > 0.1 ? C.green : g.avg < -0.1 ? C.red : C.t3} />)}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </Body>
              </Card>
            )}
          </div>
        </div>
      )}

      {sentView === 'sentences' && (
        <Card>
          <Head label={`SENTENCE-LEVEL SENTIMENT — ${sentScores.length} sentences`} accent={C.cyan} />
          <div style={{ maxHeight: 500, overflowY: 'auto' }}>
            {sentScores.map((s, i) => (
              <div key={i} style={{ display: 'flex', gap: 12, padding: '10px 14px', background: i % 2 === 0 ? 'transparent' : C.raised + '80', borderBottom: `1px solid ${C.b0}`, alignItems: 'flex-start' }}>
                <div style={{ width: 50, height: 50, borderRadius: 3, background: sentColor(s.label) + '15', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <div style={{ fontFamily: 'Syne,sans-serif', fontWeight: 800, fontSize: 13, color: sentColor(s.label) }}>{s.score > 0 ? '+' : ''}{s.score.toFixed(2)}</div>
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 11, color: C.t1, lineHeight: 1.6 }}>{s.text}</div>
                  <div style={{ display: 'flex', gap: 6, marginTop: 4 }}>
                    <span style={{ fontSize: 8, padding: '1px 6px', border: `1px solid ${sentColor(s.label)}44`, color: sentColor(s.label), borderRadius: 2, fontFamily: 'JetBrains Mono,monospace', fontWeight: 700 }}>{s.label.toUpperCase()}</span>
                    {s.posCount > 0 && <span style={{ fontSize: 8, color: C.green }}>+{s.posCount}</span>}
                    {s.negCount > 0 && <span style={{ fontSize: 8, color: C.red }}>−{s.negCount}</span>}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {sentView === 'timeline' && (
        <Card>
          <Head label="SENTIMENT ARC — TIMELINE" accent={C.cyan} right={<PinButton chart={{ id: 'nlp_sentarc', type: 'area', title: 'Sentiment Arc' }} small />} />
          <Body>
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={trend}>
                <defs>
                  <linearGradient id="sentGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={C.green} stopOpacity={0.3} />
                    <stop offset="50%" stopColor="transparent" stopOpacity={0} />
                    <stop offset="95%" stopColor={C.red} stopOpacity={0.3} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="2 2" stroke={C.b0} />
                <XAxis dataKey="idx" tick={{ fill: C.t4, fontSize: 8 }} label={{ value: 'sentence #', position: 'insideBottom', offset: -2, fill: C.t3, fontSize: 8 }} />
                <YAxis domain={[-1, 1]} tick={{ fill: C.t4, fontSize: 8 }} />
                <Tooltip content={<ChartTip />} />
                <Area type="monotone" dataKey="avg" stroke={C.cyan} fill="url(#sentGrad)" strokeWidth={2} name="sentiment" />
              </AreaChart>
            </ResponsiveContainer>
          </Body>
        </Card>
      )}

      {/* Per-doc table always visible below */}
      <Card>
        <Head label={`PER-DOCUMENT SCORES — ${filtered.length} docs`} accent={C.pink} />
        <Body style={{ paddingBottom: 0 }}>
          <div style={{ display: 'flex', gap: 8, marginBottom: 10, flexWrap: 'wrap', alignItems: 'center' }}>
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search text…"
              style={{ flex: 1, minWidth: 180, background: C.deep, border: `1px solid ${C.b2}`, color: C.t1, padding: '6px 10px', fontSize: 11, fontFamily: 'JetBrains Mono,monospace', borderRadius: 3, outline: 'none' }} />
            {['all', 'positive', 'neutral', 'negative'].map(f => (
              <button key={f} onClick={() => setFilter(f)} style={{ padding: '5px 10px', border: `1px solid ${filter === f ? sentColor(f === 'all' ? 'neutral' : f) : C.b1}`, background: filter === f ? sentColor(f === 'all' ? 'neutral' : f) + '18' : 'transparent', color: filter === f ? sentColor(f === 'all' ? 'neutral' : f) : C.t3, fontSize: 9, borderRadius: 3, cursor: 'pointer', fontFamily: 'JetBrains Mono,monospace', fontWeight: 700 }}>{f.toUpperCase()}</button>
            ))}
          </div>
        </Body>
        <div style={{ maxHeight: 400, overflowY: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
            <thead style={{ position: 'sticky', top: 0, zIndex: 1 }}><tr style={{ background: C.deep }}>
              {['#', 'TEXT PREVIEW', 'LABEL', 'SCORE', 'POS', 'NEG'].map(h => <th key={h} style={TH}>{h}</th>)}
            </tr></thead>
            <tbody>
              {filtered.slice(0, 120).map((s, i) => (
                <tr key={s.idx} style={{ borderBottom: `1px solid ${C.b0}`, background: i % 2 === 0 ? 'transparent' : C.raised + '80' }}>
                  <td style={{ ...TD, color: C.t4 }}>{s.idx + 1}</td>
                  <td style={{ ...TD, color: C.t2, maxWidth: 360 }}><div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.text.slice(0, 130)}</div></td>
                  <td style={{ ...TD }}><span style={{ color: sentColor(s.label), fontWeight: 700, fontSize: 9, padding: '2px 6px', border: `1px solid ${sentColor(s.label)}44`, borderRadius: 2 }}>{s.label.toUpperCase()}</span></td>
                  <td style={{ ...TD, color: sentColor(s.label), fontWeight: 700 }}>{s.score}</td>
                  <td style={{ ...TD, color: C.green }}>{s.posCount}</td>
                  <td style={{ ...TD, color: C.red }}>{s.negCount}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// ENTITIES TAB
// ─────────────────────────────────────────────────────────────────────────────
function EntitiesTab({ texts }) {
  const ents = useMemo(() => texts.length ? extractEntities(texts) : {}, [texts]);
  const rows1 = [['PERSON', C.cyan, '👤'], ['ORG', C.green, '🏢'], ['LOCATION', C.gold, '📍']];
  const rows2 = [['DATE', C.violet, '📅'], ['MONEY', C.gold, '💰'], ['PERCENT', C.orange, '📊']];
  const rows3 = [['EMAIL', C.cyan, '✉'], ['URL', C.blue, '🔗'], ['HASHTAG', C.pink, '#'], ['MENTION', C.violet, '@']];

  const EntityPanel = ({ type, color, icon }) => (
    <Card>
      <Head label={`${icon} ${type}`} accent={color} sub={`${(ents[type] || []).length} found`} />
      <Body>
        {(ents[type] || []).length > 0
          ? <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, maxHeight: 120, overflowY: 'auto' }}>
              {(ents[type] || []).map(e => <span key={e} style={{ padding: '4px 10px', background: color + '15', border: `1px solid ${color}44`, fontSize: 11, color, borderRadius: 2 }}>{icon} {e}</span>)}
            </div>
          : <span style={{ color: C.t4, fontSize: 11 }}>None detected</span>
        }
      </Body>
    </Card>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {/* Summary row */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 10 }}>
        {[['PERSON', C.cyan], ['ORG', C.green], ['LOCATION', C.gold], ['DATE', C.violet], ['MONEY', C.gold]].map(([t, c]) => (
          <MB key={t} label={t} value={(ents[t] || []).length} color={c} />
        ))}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 10 }}>
        {[['PERCENT', C.orange], ['EMAIL', C.cyan], ['URL', C.blue], ['HASHTAG', C.pink], ['MENTION', C.violet]].map(([t, c]) => (
          <MB key={t} label={t} value={(ents[t] || []).length} color={c} />
        ))}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 14 }}>
        {rows1.map(([t, c, i]) => <EntityPanel key={t} type={t} color={c} icon={i} />)}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 14 }}>
        {rows2.map(([t, c, i]) => <EntityPanel key={t} type={t} color={c} icon={i} />)}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14 }}>
        {rows3.map(([t, c, i]) => <EntityPanel key={t} type={t} color={c} icon={i} />)}
      </div>
      <InfoBox color={C.gold}>NER uses pattern-matching rules, not a trained ML model. Results are approximate — accuracy improves with formal, well-structured text.</InfoBox>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// KEYWORDS TAB
// ─────────────────────────────────────────────────────────────────────────────
function KeywordsTab({ texts, col }) {
  const keywords = useMemo(() => texts.length ? extractKeywords(texts, 30) : [], [texts]);
  if (!keywords.length) return <div style={{ padding: 40, textAlign: 'center', color: C.t4 }}>No keywords extracted.</div>;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <WordCloud words={keywords} title={`KEYWORD CLOUD — TF-IDF WEIGHTED · ${col}`} accent={C.cyan} />
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
        <Card>
          <Head label="TF-IDF SCORES" accent={C.cyan} right={<PinButton chart={{ id: 'nlp_kw', type: 'bar', title: 'TF-IDF Keywords', subtitle: col }} small />} />
          <Body>
            <ResponsiveContainer width="100%" height={380}>
              <BarChart data={keywords.slice(0, 20)} layout="vertical" barCategoryGap="8%" margin={{ top: 0, right: 70, bottom: 0, left: 0 }}>
                <CartesianGrid strokeDasharray="2 2" stroke={C.b0} horizontal={false} />
                <XAxis type="number" tick={{ fill: C.t4, fontSize: 9 }} />
                <YAxis dataKey="word" type="category" tick={{ fill: C.t2, fontSize: 10 }} width={100} />
                <Tooltip content={<ChartTip />} />
                <Bar dataKey="tfidf" name="TF-IDF" radius={[0, 3, 3, 0]}>
                  {keywords.map((_, i) => <Cell key={i} fill={PALETTE[i % PALETTE.length]} opacity={0.85} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </Body>
        </Card>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <Card>
            <Head label="KEYWORD TABLE" accent={C.cyan} />
            <div style={{ maxHeight: 380, overflowY: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                <thead style={{ position: 'sticky', top: 0, zIndex: 1 }}><tr style={{ background: C.deep }}>
                  {['#', 'WORD', 'TF-IDF', 'FREQ', 'BAR'].map(h => <th key={h} style={TH}>{h}</th>)}
                </tr></thead>
                <tbody>
                  {keywords.map((kw, i) => (
                    <tr key={kw.word} style={{ borderBottom: `1px solid ${C.b0}` }}>
                      <td style={{ ...TD, color: C.t4 }}>{i + 1}</td>
                      <td style={{ ...TD, color: PALETTE[i % PALETTE.length], fontWeight: 700, borderLeft: `2px solid ${PALETTE[i % PALETTE.length]}` }}>{kw.word}</td>
                      <td style={{ ...TD, color: C.cyan, fontWeight: 700 }}>{kw.tfidf}</td>
                      <td style={{ ...TD, color: C.t2 }}>{kw.freq}</td>
                      <td style={{ padding: '6px 11px', width: 100 }}><MiniBar pct={kw.tfidf / keywords[0].tfidf * 100} color={PALETTE[i % PALETTE.length]} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// TOPICS TAB
// ─────────────────────────────────────────────────────────────────────────────
function TopicsTab({ texts, col }) {
  const [nTopics, setNTopics] = useState(5);
  const [topics,  setTopics]  = useState(null);
  const [running, setRunning] = useState(false);
  const rep = useMemo(() => texts.length ? getRepetition(texts) : null, [texts]);

  const run = useCallback(async () => {
    setRunning(true);
    await new Promise(r => setTimeout(r, 20));
    setTopics(extractTopics(texts, nTopics));
    setRunning(false);
  }, [texts, nTopics]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <Card>
        <Head label="TOPIC MODEL CONFIGURATION" accent={C.gold} />
        <Body>
          <div style={{ fontSize: 11, color: C.t2, marginBottom: 12, lineHeight: 1.75 }}>
            Pure-JS LDA-style topic modeling via word co-occurrence. Seeds each topic on the highest-frequency unused term, then expands via co-occurrence strength — zero external libraries.
          </div>
          <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <div>
              <Label>NUMBER OF TOPICS</Label>
              <div style={{ display: 'flex', gap: 6 }}>
                {[3, 4, 5, 6, 7, 8].map(n => (
                  <button key={n} onClick={() => setNTopics(n)} style={{ padding: '5px 10px', border: `1px solid ${nTopics === n ? C.gold : C.b1}`, background: nTopics === n ? C.gold + '18' : 'transparent', color: nTopics === n ? C.gold : C.t3, fontSize: 10, borderRadius: 3, cursor: 'pointer', fontFamily: 'JetBrains Mono,monospace', fontWeight: 700 }}>{n}</button>
                ))}
              </div>
            </div>
            <Btn onClick={run} color={C.gold} disabled={running}>{running ? '⟳ MODELING…' : '▶ EXTRACT TOPICS'}</Btn>
          </div>
        </Body>
      </Card>

      {topics && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 12 }}>
            {topics.map(t => (
              <div key={t.id} style={{ padding: 16, background: C.surface, border: `1px solid ${t.color}33`, borderRadius: 4 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                  <span style={{ color: t.color, fontSize: 20 }}>⬢</span>
                  <div style={{ fontFamily: 'Syne,sans-serif', fontWeight: 700, fontSize: 14, color: t.color }}>{t.label}</div>
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 10 }}>
                  {t.words.map((w, wi) => (
                    <span key={w} style={{ padding: '3px 9px', background: t.color + '15', border: `1px solid ${t.color}44`, fontSize: `${12 - wi * 0.4}px`, color: t.color, borderRadius: 2, opacity: 1 - wi * 0.05 }}>{w}</span>
                  ))}
                </div>
                <MiniBar pct={t.docCount / texts.length * 100} color={t.color} height={4} />
                <div style={{ fontSize: 9, color: C.t4, marginTop: 3 }}>{t.docCount}/{texts.length} docs ({(t.docCount / texts.length * 100).toFixed(1)}%)</div>
              </div>
            ))}
          </div>
          <Card>
            <Head label="TOPIC COVERAGE" accent={C.gold} right={<PinButton chart={{ id: 'nlp_topics', type: 'bar', title: 'Topic Coverage', subtitle: col }} small />} />
            <Body>
              <ResponsiveContainer width="100%" height={180}>
                <BarChart data={topics.map(t => ({ label: t.label, count: t.docCount }))} barCategoryGap="22%">
                  <CartesianGrid strokeDasharray="2 2" stroke={C.b0} />
                  <XAxis dataKey="label" tick={{ fill: C.t3, fontSize: 10 }} />
                  <YAxis tick={{ fill: C.t4, fontSize: 8 }} />
                  <Tooltip content={<ChartTip />} />
                  <Bar dataKey="count" name="documents" radius={[3, 3, 0, 0]}>
                    {topics.map(t => <Cell key={t.id} fill={t.color} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Body>
          </Card>
          <InfoBox color={C.cyan}>Topics extracted via co-occurrence clustering. Lightweight LDA-lite — not a full probabilistic model. Results are indicative.</InfoBox>
        </>
      )}

      {/* Repetition always shown */}
      {rep && (
        <Card>
          <Head label="REPETITION & REDUNDANCY" accent={C.orange} sub={`${rep.repeatedPhrases.length} repeated phrases · redundancy ${rep.redundancyScore}%`} />
          <Body>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              <div>
                <div style={{ fontSize: 9, color: C.t3, fontFamily: 'JetBrains Mono,monospace', letterSpacing: '.1em', marginBottom: 8 }}>TOP REPEATED PHRASES</div>
                {rep.repeatedPhrases.slice(0, 12).map((p, i) => (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: `1px solid ${C.b0}`, fontSize: 11 }}>
                    <span style={{ color: C.t1, flex: 1 }}>"{p.phrase}"</span>
                    <span style={{ color: C.orange, fontWeight: 700, marginLeft: 8 }}>×{p.count}</span>
                  </div>
                ))}
              </div>
              <div>
                <MB label="REDUNDANCY SCORE" value={`${rep.redundancyScore}%`} color={C.orange} sub="near-duplicate sentences" />
                {rep.nearDuplicates.length > 0 && (
                  <div style={{ marginTop: 12 }}>
                    <div style={{ fontSize: 9, color: C.t3, fontFamily: 'JetBrains Mono,monospace', marginBottom: 8 }}>NEAR-DUPLICATE SENTENCES</div>
                    {rep.nearDuplicates.map((nd, i) => (
                      <div key={i} style={{ padding: 8, background: C.raised, borderRadius: 2, marginBottom: 6, border: `1px solid ${C.orange}22` }}>
                        <div style={{ fontSize: 9, color: C.orange, marginBottom: 3 }}>sim={nd.sim} — #{nd.i + 1} and #{nd.j + 1}</div>
                        <div style={{ fontSize: 10, color: C.t3 }}>"{nd.s1.slice(0, 100)}"</div>
                        <div style={{ fontSize: 10, color: C.t3, marginTop: 2 }}>"{nd.s2.slice(0, 100)}"</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </Body>
        </Card>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// SUMMARY TAB
// ─────────────────────────────────────────────────────────────────────────────
function SummaryTab({ texts }) {
  const [summaryN, setSummaryN] = useState(5);
  const allSents = useMemo(() => texts.flatMap(t => tokenizeSentences(t)), [texts]);
  const result   = useMemo(() => texts.length ? extractiveSummary(texts, summaryN) : null, [texts, summaryN]);
  if (!result) return <div style={{ padding: 40, textAlign: 'center', color: C.t4 }}>No data.</div>;
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 360px', gap: 14 }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <Card>
          <Head label="EXTRACTIVE SUMMARY" accent={C.violet} sub={`top ${summaryN} key sentences`} />
          <Body>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 14 }}>
              <span style={{ fontSize: 11, color: C.t3 }}>Sentences:</span>
              <input type="range" min={1} max={Math.min(10, allSents.length || 10)} value={summaryN} onChange={e => setSummaryN(+e.target.value)} style={{ accentColor: C.violet, width: 120 }} />
              <span style={{ fontSize: 11, color: C.violet, fontWeight: 700 }}>{summaryN}</span>
            </div>
            {result.sentences.map((s, i) => (
              <div key={i} style={{ padding: '12px 14px', background: C.raised, borderLeft: `3px solid ${C.violet}`, marginBottom: 8, borderRadius: '0 3px 3px 0' }}>
                <div style={{ fontSize: 9, color: C.violet, marginBottom: 5, fontFamily: 'JetBrains Mono,monospace', letterSpacing: '.08em' }}>KEY SENTENCE {i + 1}</div>
                <div style={{ fontSize: 13, color: C.t1, lineHeight: 1.65 }}>{s}</div>
              </div>
            ))}
          </Body>
        </Card>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <Card>
          <Head label="SENTENCE IMPORTANCE SCORES" accent={C.violet} right={<PinButton chart={{ id: 'nlp_summary', type: 'bar', title: 'Sentence Importance' }} small />} />
          <Body>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={result.scores.slice(0, 25)} layout="vertical">
                <XAxis type="number" tick={{ fill: C.t4, fontSize: 9 }} />
                <YAxis type="category" dataKey="idx" tick={{ fill: C.t3, fontSize: 9 }} width={35} />
                <Tooltip content={<ChartTip />} />
                <Bar dataKey="score" name="importance" radius={[0, 2, 2, 0]}>
                  {result.scores.slice(0, 25).map((s, i) => <Cell key={i} fill={s.score > 3 ? C.violet : s.score > 2 ? C.cyan : C.t3} opacity={0.85} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </Body>
        </Card>
        <InfoBox color={C.violet}>Extractive summary selects highest-scoring sentences by term frequency weighting. Not generative — exact sentences from source.</InfoBox>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// SIMILARITY TAB  (query search + find similar + doc compare)
// ─────────────────────────────────────────────────────────────────────────────
function SimilarityTab({ texts, col, config }) {
  const [mode,     setMode]    = useState('search');
  const [query,    setQuery]   = useState('');
  const [docIdx,   setDocIdx]  = useState(0);
  const [results,  setResults] = useState([]);
  const [compA,    setCompA]   = useState('');
  const [compB,    setCompB]   = useState('');
  const [docSim,   setDocSim]  = useState(null);

  const { vecs, sample } = useMemo(() => {
    const s = texts.slice(0, 500);
    return { vecs: computeTFIDF(s.map(t => tokenize(t, config.useStopwords))), sample: s };
  }, [texts, config]);

  const buildQV = useCallback(q => {
    const qtoks = tokenize(q, config.useStopwords);
    const df = {};
    vecs.forEach(v => Object.keys(v).forEach(w => { df[w] = (df[w] || 0) + 1; }));
    const N = sample.length, qv = {};
    qtoks.forEach(t => { const idf = Math.log((N + 1) / ((df[t] || 0) + 1)) + 1; qv[t] = (qv[t] || 0) + idf / qtoks.length; });
    return qv;
  }, [vecs, sample, config]);

  const doSearch    = () => { if (!query.trim()) return; const qv = buildQV(query); setResults(sample.map((t, i) => ({ idx: i, text: t, sim: cosineSim(qv, vecs[i]) })).sort((a, b) => b.sim - a.sim).slice(0, 20)); };
  const findSimilar = () => { const pv = vecs[docIdx]; if (!pv) return; setResults(sample.map((t, i) => ({ idx: i, text: t, sim: i === docIdx ? 0 : cosineSim(pv, vecs[i]) })).sort((a, b) => b.sim - a.sim).slice(0, 15)); };
  const doCompare   = () => {
    if (!compA.trim() || !compB.trim()) return;
    const vA = buildQV(compA), vB = buildQV(compB);
    const tA = new Set(tokenize(compA, true)), tB = new Set(tokenize(compB, true));
    const inter = [...tA].filter(w => tB.has(w));
    const jaccard = +(inter.length / (tA.size + tB.size - inter.length + 1e-9)).toFixed(4);
    const overlap = +(inter.length / Math.min(tA.size, tB.size + 1e-9)).toFixed(4);
    setDocSim({ cosine: cosineSim(vA, vB), jaccard, overlap, shared: inter.slice(0, 20), uniqueA: [...tA].filter(w => !tB.has(w)).slice(0, 15), uniqueB: [...tB].filter(w => !tA.has(w)).slice(0, 15) });
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={{ display: 'flex', gap: 4 }}>
        {[['search', '🔍 QUERY SEARCH'], ['doc', '📄 FIND SIMILAR DOC'], ['compare', '⊞ COMPARE 2 DOCS']].map(([m, l]) => (
          <button key={m} onClick={() => { setMode(m); setResults([]); setDocSim(null); }} style={{ padding: '8px 16px', border: `1px solid ${mode === m ? C.blue : C.b0}`, background: mode === m ? C.blue + '15' : C.raised, color: mode === m ? C.blue : C.t2, fontSize: 10, cursor: 'pointer', borderRadius: 3, fontFamily: 'JetBrains Mono,monospace', fontWeight: mode === m ? 700 : 400, transition: 'all .12s' }}>{l}</button>
        ))}
      </div>

      {mode === 'search' && (
        <Card>
          <Head label="QUERY SEARCH — TF-IDF COSINE" accent={C.blue} sub={`${Math.min(texts.length, 500)} docs indexed`} />
          <Body>
            <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
              <div style={{ flex: 1 }}>
                <Label>ENTER QUERY TEXT</Label>
                <input value={query} onChange={e => setQuery(e.target.value)} onKeyDown={e => e.key === 'Enter' && doSearch()} placeholder="Type any phrase or concept…"
                  style={{ width: '100%', background: C.deep, border: `1px solid ${C.b2}`, color: C.t1, padding: '8px 12px', fontSize: 11, fontFamily: 'JetBrains Mono,monospace', borderRadius: 3, outline: 'none' }} />
              </div>
              <Btn onClick={doSearch} color={C.blue}>SEARCH →</Btn>
            </div>
          </Body>
        </Card>
      )}

      {mode === 'doc' && (
        <Card>
          <Head label="FIND SIMILAR DOCUMENTS" accent={C.blue} />
          <Body>
            <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
              <div>
                <Label>PIVOT DOC INDEX (0–{Math.min(texts.length - 1, 499)})</Label>
                <input type="number" min={0} max={Math.min(texts.length - 1, 499)} value={docIdx} onChange={e => setDocIdx(+e.target.value)}
                  style={{ background: C.deep, border: `1px solid ${C.b2}`, color: C.t1, padding: '7px 10px', fontSize: 11, fontFamily: 'JetBrains Mono,monospace', borderRadius: 3, outline: 'none', width: 100 }} />
              </div>
              {docIdx < texts.length && <div style={{ flex: 1, padding: '8px 10px', background: C.raised, borderRadius: 3, fontSize: 10, color: C.t2, wordBreak: 'break-word', maxWidth: 440 }}>{texts[docIdx]?.slice(0, 130)}…</div>}
              <Btn onClick={findSimilar} color={C.blue}>FIND →</Btn>
            </div>
          </Body>
        </Card>
      )}

      {mode === 'compare' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            {[['DOCUMENT A', compA, setCompA, C.cyan], ['DOCUMENT B', compB, setCompB, C.violet]].map(([label, val, setter, color]) => (
              <Card key={label}>
                <Head label={label} accent={color} />
                <Body>
                  <textarea value={val} onChange={e => setter(e.target.value)} placeholder="Paste text here…" rows={7}
                    style={{ width: '100%', background: C.deep, border: `1px solid ${C.b2}`, color: C.t1, padding: 10, fontSize: 11, fontFamily: 'inherit', borderRadius: 3, resize: 'vertical', outline: 'none', boxSizing: 'border-box' }} />
                </Body>
              </Card>
            ))}
          </div>
          <button onClick={doCompare} style={{ width: '100%', padding: 11, background: C.violet + '18', border: `1px solid ${C.violet}44`, color: C.violet, fontSize: 11, cursor: 'pointer', borderRadius: 3, fontFamily: 'JetBrains Mono,monospace', letterSpacing: '.08em', fontWeight: 700 }}>COMPARE DOCUMENTS →</button>
          {docSim && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
                <MB label="COSINE SIMILARITY" value={docSim.cosine}  color={docSim.cosine > 0.7 ? C.green : docSim.cosine > 0.3 ? C.gold : C.red} sub={docSim.cosine > 0.7 ? 'Very similar' : docSim.cosine > 0.3 ? 'Somewhat similar' : 'Dissimilar'} />
                <MB label="JACCARD INDEX"     value={docSim.jaccard} color={C.cyan}   sub="word overlap" />
                <MB label="OVERLAP COEFF."   value={docSim.overlap}  color={C.violet} sub="relative to smaller" />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 14 }}>
                {[{ label: 'SHARED VOCABULARY', words: docSim.shared, color: C.green }, { label: 'UNIQUE TO DOC A', words: docSim.uniqueA, color: C.cyan }, { label: 'UNIQUE TO DOC B', words: docSim.uniqueB, color: C.violet }].map(g => (
                  <Card key={g.label}>
                    <Head label={g.label} accent={g.color} sub={`${g.words.length} words`} />
                    <Body>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                        {g.words.map(w => <span key={w} style={{ padding: '3px 9px', background: g.color + '15', border: `1px solid ${g.color}33`, fontSize: 11, color: g.color, borderRadius: 2 }}>{w}</span>)}
                      </div>
                    </Body>
                  </Card>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {results.length > 0 && mode !== 'compare' && (
        <Card>
          <Head label={`TOP ${results.length} SIMILAR DOCUMENTS`} accent={C.blue} />
          <div style={{ maxHeight: 480, overflowY: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
              <thead style={{ position: 'sticky', top: 0, zIndex: 1 }}><tr style={{ background: C.deep }}>
                {['RANK', 'DOC #', 'SIMILARITY', 'TEXT PREVIEW'].map(h => <th key={h} style={TH}>{h}</th>)}
              </tr></thead>
              <tbody>
                {results.map((r, i) => (
                  <tr key={r.idx} style={{ borderBottom: `1px solid ${C.b0}`, background: i === 0 ? C.blue + '08' : 'transparent' }}>
                    <td style={{ ...TD, color: i === 0 ? C.gold : C.t4, fontWeight: 700 }}>{i === 0 ? '★' : i + 1}</td>
                    <td style={{ ...TD, color: C.blue }}>#{r.idx + 1}</td>
                    <td style={{ padding: '6px 11px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <div style={{ width: 70, height: 5, background: C.b1, borderRadius: 2 }}>
                          <div style={{ height: '100%', width: `${r.sim * 100}%`, background: r.sim > 0.5 ? C.green : r.sim > 0.2 ? C.gold : C.blue, borderRadius: 2 }} />
                        </div>
                        <span style={{ fontFamily: 'JetBrains Mono,monospace', fontSize: 10, color: r.sim > 0.5 ? C.green : r.sim > 0.2 ? C.gold : C.t2, fontWeight: r.sim > 0.3 ? 700 : 400 }}>{r.sim}</span>
                      </div>
                    </td>
                    <td style={{ ...TD, color: C.t2, maxWidth: 400 }}><div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.text.slice(0, 160)}</div></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// REPORT TAB
// ─────────────────────────────────────────────────────────────────────────────
const NTAGS = [{ id: 'finding', label: 'FINDING', color: C.green }, { id: 'insight', label: 'INSIGHT', color: C.pink }, { id: 'hypothesis', label: 'HYPOTHESIS', color: C.violet }, { id: 'warning', label: 'WARNING', color: C.red }, { id: 'todo', label: 'TODO', color: C.gold }];
const ntag  = id => NTAGS.find(t => t.id === id) || NTAGS[0];

const NLP_SECS = [
  { id: 'overview',   label: 'Corpus Overview',      required: true  },
  { id: 'tokens',     label: 'Token Analysis',        required: false },
  { id: 'sentiment',  label: 'Sentiment Analysis',    required: false },
  { id: 'readability',label: 'Readability',           required: false },
  { id: 'entities',   label: 'Named Entities',        required: false },
  { id: 'topics',     label: 'Topic Modeling',        required: false },
  { id: 'charts',     label: 'Pinned Charts',         required: false },
  { id: 'conclusion', label: 'Insights & Next Steps', required: true  },
];

function NoteModal({ onSave, onClose }) {
  const [text, setText] = useState('');
  const [tag,  setTag]  = useState('finding');
  const tm = ntag(tag);
  return (
    <div onClick={e => e.target === e.currentTarget && onClose()} style={{ position: 'fixed', inset: 0, background: 'rgba(2,5,8,.88)', zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: C.surface, border: `1px solid ${C.b2}`, borderRadius: 6, width: 480, maxWidth: '96vw', overflow: 'hidden' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '11px 16px', background: C.deep, borderBottom: `1px solid ${C.b0}` }}>
          <span style={{ fontFamily: 'JetBrains Mono,monospace', fontSize: 10, color: C.pink, letterSpacing: '.12em', fontWeight: 700 }}>✎ ADD NOTE TO REPORT</span>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: C.t3, cursor: 'pointer', fontSize: 20 }}>×</button>
        </div>
        <div style={{ display: 'flex', gap: 5, padding: '12px 16px 8px', flexWrap: 'wrap' }}>
          {NTAGS.map(t => <button key={t.id} onClick={() => setTag(t.id)} style={{ padding: '4px 10px', border: `1px solid ${tag === t.id ? t.color : C.b1}`, background: tag === t.id ? t.color + '20' : 'transparent', color: tag === t.id ? t.color : C.t3, fontFamily: 'JetBrains Mono,monospace', fontSize: 9, fontWeight: 700, borderRadius: 2, cursor: 'pointer' }}>{t.label}</button>)}
        </div>
        <div style={{ margin: '0 16px 8px', padding: '6px 10px', borderLeft: `3px solid ${tm.color}`, background: C.raised, borderRadius: 2 }}>
          <span style={{ fontSize: 8, color: tm.color, fontFamily: 'JetBrains Mono,monospace', fontWeight: 700 }}>{tm.label}</span>
        </div>
        <div style={{ padding: '0 16px' }}>
          <textarea value={text} onChange={e => setText(e.target.value)} placeholder="Your insight, finding, or hypothesis…" style={{ width: '100%', minHeight: 80, background: C.deep, border: `1px solid ${C.b2}`, color: C.t1, padding: 10, fontSize: 12, fontFamily: 'inherit', borderRadius: 3, resize: 'vertical', outline: 'none', boxSizing: 'border-box' }} />
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, padding: '12px 16px' }}>
          <button onClick={onClose} style={{ padding: '6px 16px', background: 'none', border: `1px solid ${C.b1}`, color: C.t3, borderRadius: 3, cursor: 'pointer', fontFamily: 'JetBrains Mono,monospace', fontSize: 9 }}>CANCEL</button>
          <button onClick={() => { if (text.trim()) { onSave({ text: text.trim(), tag, id: `n_${Date.now()}`, ts: new Date().toISOString() }); onClose(); } }} style={{ padding: '6px 18px', background: tm.color, border: 'none', color: C.void, borderRadius: 3, cursor: 'pointer', fontFamily: 'JetBrains Mono,monospace', fontSize: 9, fontWeight: 700 }}>ADD</button>
        </div>
      </div>
    </div>
  );
}

function ReportTab({ experiment, dashboardCards, texts, col, config }) {
  const [secs,    setSecs]    = useState(new Set(NLP_SECS.map(s => s.id)));
  const [order,   setOrder]   = useState(NLP_SECS.map(s => s.id));
  const [notes,   setNotes]   = useState([]);
  const [modal,   setModal]   = useState(false);
  const [dragSec, setDragSec] = useState(null);
  const reportRef = useRef();

  const has = id => secs.has(id);
  const toggle = (id, req) => { if (req) return; setSecs(p => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; }); };
  const drop = toId => {
    if (!dragSec || dragSec === toId) { setDragSec(null); return; }
    setOrder(prev => { const a = [...prev]; const si = a.indexOf(dragSec); const ti = a.indexOf(toId); if (si < 0 || ti < 0) return prev; a.splice(si, 1); a.splice(ti, 0, dragSec); return a; });
    setDragSec(null);
  };

  const stats = useMemo(() => {
    if (!texts.length) return null;
    const bs = getBasicStats(texts);
    const rd = fleschAll(texts);
    const freq = {};
    tokenize(texts.join(' '), true).forEach(w => { freq[w] = (freq[w] || 0) + 1; });
    const topWords = Object.entries(freq).sort((a, b) => b[1] - a[1]).slice(0, 15);
    const bigrams  = nGrams(texts.flatMap(t => tokenize(t, false)), 2).slice(0, 10);
    const sents = texts.map(t => sentimentScore(t));
    const avgSent = +(sents.reduce((a, s) => a + s.score, 0) / sents.length).toFixed(4);
    const lang = detectLang(texts.slice(0, 20).join(' '));
    const ents = extractEntities(texts);
    return { ...bs, rd, topWords, bigrams, avgSent, pos: sents.filter(s => s.label === 'positive').length, neg: sents.filter(s => s.label === 'negative').length, lang, ents };
  }, [texts, config]);

  const exportMD = () => {
    if (!stats) return;
    const overallLabel = stats.avgSent > 0.1 ? 'Positive' : stats.avgSent < -0.1 ? 'Negative' : 'Neutral';
    const md = [`# NLP ANALYSIS REPORT\n**${experiment.name}** · Column: \`${col}\` · ${new Date().toDateString()}\n`,
      `## Corpus Overview\n- Documents: ${stats.paragraphs}\n- Vocabulary: ${stats.unique.toLocaleString()} unique tokens\n- Total Words: ${stats.words.toLocaleString()}\n- Avg Words/Doc: ${stats.avgSentLen}\n- Language: ${stats.lang}\n- Readability: ${stats.rd.grade} (Flesch ${stats.rd.flesch})\n- Lexical Diversity: ${(stats.lexDiv * 100).toFixed(1)}%\n`,
      `## Sentiment\n- Overall: ${overallLabel} (avg score: ${stats.avgSent})\n- Positive docs: ${stats.pos} (${(stats.pos / stats.paragraphs * 100).toFixed(1)}%)\n- Negative docs: ${stats.neg} (${(stats.neg / stats.paragraphs * 100).toFixed(1)}%)\n`,
      `## Top 15 Terms\n${stats.topWords.map(([w, c], i) => `${i + 1}. **${w}** — ${c}`).join('\n')}\n`,
      `## Entities Found\nPersons: ${stats.ents.PERSON.length} · Orgs: ${stats.ents.ORG.length} · Locations: ${stats.ents.LOCATION.length} · Dates: ${stats.ents.DATE.length}\n`,
      notes.length > 0 ? `## Research Notes\n${notes.map(n => `**[${n.tag.toUpperCase()}]** ${n.text}`).join('\n')}\n` : '',
      `## Conclusion\nNLP analysis of \`${col}\` complete. ${stats.paragraphs} documents, ${stats.unique} unique terms, ${stats.rd.grade} readability. Sentiment: ${overallLabel} (${stats.avgSent}).`,
    ].join('\n');
    const a = Object.assign(document.createElement('a'), { href: URL.createObjectURL(new Blob([md], { type: 'text/markdown' })), download: `nlp-report-${experiment.name}.md` });
    a.click();
  };

  const exportHTML = () => {
    const body = reportRef.current?.innerHTML || '';
    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>NLP Report</title><style>*{box-sizing:border-box}body{font-family:system-ui,sans-serif;background:#020508;color:#e2e8f0;padding:48px 40px;max-width:1040px;margin:0 auto;line-height:1.8}h1{font-size:26px;font-weight:800;color:#f472b6}h2{font-size:16px;font-weight:800;color:#a78bfa;margin:24px 0 8px}</style></head><body>${body}</body></html>`;
    const a = Object.assign(document.createElement('a'), { href: URL.createObjectURL(new Blob([html], { type: 'text/html' })), download: `nlp-report-${experiment.name}.html` });
    a.click();
  };

  if (!stats) return <div style={{ padding: 40, textAlign: 'center', color: C.t4 }}>No text data. Select a column in SETUP first.</div>;

  const overallColor = stats.avgSent > 0.15 ? C.green : stats.avgSent < -0.15 ? C.red : C.t3;
  const overallLabel = stats.avgSent > 0.15 ? 'Positive 😊' : stats.avgSent < -0.15 ? 'Negative 😞' : 'Neutral 😐';

  const DragSec = ({ id, title, color, children, req }) => (
    <div draggable onDragStart={() => setDragSec(id)} onDragOver={e => e.preventDefault()} onDrop={e => { e.preventDefault(); drop(id); }}
      style={{ marginBottom: 28, opacity: dragSec === id ? 0.4 : 1, transition: 'opacity .15s' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
        <span style={{ cursor: 'grab', color: C.t4, fontSize: 15, userSelect: 'none' }}>⠿</span>
        <h2 style={{ fontFamily: 'Syne,sans-serif', fontWeight: 800, fontSize: 16, color, margin: 0, flex: 1 }}>{title}</h2>
        {!req && <button onClick={() => toggle(id, false)} style={{ background: 'none', border: `1px solid ${C.b1}`, color: C.t4, fontSize: 11, borderRadius: 2, cursor: 'pointer', padding: '1px 7px', fontFamily: 'JetBrains Mono,monospace' }}>HIDE</button>}
      </div>
      <div style={{ height: 1, background: `linear-gradient(90deg,${color}55 0%,transparent 80%)`, marginBottom: 14 }} />
      {children}
    </div>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {/* Controls */}
      <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap', padding: '10px 14px', background: C.surface, border: `1px solid ${C.b1}`, borderRadius: 4 }}>
        <div style={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
          {NLP_SECS.map(s => (
            <button key={s.id} onClick={() => toggle(s.id, s.required)} style={{ padding: '3px 8px', border: `1px solid ${has(s.id) ? C.pink : C.b1}`, background: has(s.id) ? C.pink + '15' : 'transparent', color: has(s.id) ? C.pink : C.t4, fontFamily: 'JetBrains Mono,monospace', fontSize: 8, borderRadius: 2, cursor: s.required ? 'default' : 'pointer', opacity: s.required ? 0.6 : 1 }}>
              {has(s.id) ? '✓' : '+'} {s.label}{s.required ? ' *' : ''}
            </button>
          ))}
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
          <button onClick={() => setModal(true)} style={{ padding: '5px 12px', border: `1px solid ${C.gold}`, background: C.gold + '15', color: C.gold, fontFamily: 'JetBrains Mono,monospace', fontSize: 9, fontWeight: 700, borderRadius: 3, cursor: 'pointer' }}>✎ NOTE</button>
          <button onClick={exportMD}   style={{ padding: '5px 11px', border: `1px solid ${C.cyan}`,  background: C.cyan  + '15', color: C.cyan,  fontFamily: 'JetBrains Mono,monospace', fontSize: 9, fontWeight: 700, borderRadius: 3, cursor: 'pointer' }}>↓ MD</button>
          <button onClick={exportHTML} style={{ padding: '5px 11px', border: `1px solid ${C.green}`, background: C.green + '15', color: C.green, fontFamily: 'JetBrains Mono,monospace', fontSize: 9, fontWeight: 700, borderRadius: 3, cursor: 'pointer' }}>↓ HTML</button>
        </div>
      </div>

      {/* Report canvas */}
      <div ref={reportRef} style={{ background: C.surface, border: `1px solid ${C.b1}`, borderRadius: 6, padding: '32px 36px' }}>
        <h1 style={{ fontFamily: 'Syne,sans-serif', fontWeight: 800, fontSize: 26, color: C.t1, margin: '0 0 4px' }}>{experiment.name}</h1>
        <div style={{ fontSize: 9, color: C.t3, fontFamily: 'JetBrains Mono,monospace', marginBottom: 6 }}>NLP ANALYSIS REPORT · {new Date().toLocaleDateString('en', { year: 'numeric', month: 'long', day: 'numeric' })} · COLUMN: {col} · {stats.paragraphs} DOCUMENTS</div>
        <div style={{ height: 1, background: `linear-gradient(90deg,${C.pink}55 0%,transparent 80%)`, marginBottom: 28 }} />
        <div style={{ fontSize: 8, color: C.t4, letterSpacing: '.1em', marginBottom: 20, fontFamily: 'JetBrains Mono,monospace' }}>⠿ DRAG TO REORDER SECTIONS</div>

        {order.map(sid => {
          if (!has(sid)) return null;
          if (sid === 'overview') return (
            <DragSec key="overview" id="overview" title="Corpus Overview" color={C.pink} req>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 8, marginBottom: 14 }}>
                <MB label="DOCUMENTS"   value={stats.paragraphs.toLocaleString()} color={C.pink} />
                <MB label="VOCABULARY"  value={stats.unique.toLocaleString()}     color={C.cyan} />
                <MB label="WORDS"       value={stats.words.toLocaleString()}       color={C.green} />
                <MB label="AVG WORDS"   value={stats.avgSentLen}                   color={C.violet} />
                <MB label="READABILITY" value={stats.rd.grade}                     color={stats.rd.flesch > 60 ? C.green : stats.rd.flesch > 40 ? C.gold : C.red} />
              </div>
              <p style={{ fontSize: 12, color: C.t2, lineHeight: 1.8, margin: 0 }}>
                Corpus in column <strong style={{ color: C.pink }}>{col}</strong> contains <strong style={{ color: C.cyan }}>{stats.paragraphs} documents</strong> with <strong style={{ color: C.green }}>{stats.unique.toLocaleString()} unique terms</strong>. Language: <strong style={{ color: C.gold }}>{stats.lang}</strong>. Readability: <strong>{stats.rd.grade}</strong> (Flesch {stats.rd.flesch}). Lexical density: <strong>{(stats.lexDiv * 100).toFixed(1)}%</strong>.
              </p>
            </DragSec>
          );
          if (sid === 'tokens') return (
            <DragSec key="tokens" id="tokens" title="Token Analysis" color={C.cyan}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                <div>
                  <div style={{ fontSize: 9, color: C.t3, fontFamily: 'JetBrains Mono,monospace', marginBottom: 8 }}>TOP 15 TERMS</div>
                  {stats.topWords.map(([w, c], i) => (
                    <div key={w} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '4px 0', borderBottom: `1px solid ${C.b0}` }}>
                      <span style={{ fontFamily: 'JetBrains Mono,monospace', fontSize: 11, color: PALETTE[i % PALETTE.length], fontWeight: 700, minWidth: 100 }}>{w}</span>
                      <span style={{ fontSize: 11, color: C.t2, minWidth: 40 }}>{c}</span>
                      <div style={{ flex: 1 }}><MiniBar pct={c / stats.topWords[0][1] * 100} color={PALETTE[i % PALETTE.length]} /></div>
                    </div>
                  ))}
                </div>
                <div>
                  <div style={{ fontSize: 9, color: C.t3, fontFamily: 'JetBrains Mono,monospace', marginBottom: 8 }}>TOP BIGRAMS</div>
                  {stats.bigrams.map(([g, c], i) => (
                    <div key={g} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '4px 0', borderBottom: `1px solid ${C.b0}` }}>
                      <span style={{ fontFamily: 'JetBrains Mono,monospace', fontSize: 10, color: C.t2, flex: 1 }}>"{g}"</span>
                      <span style={{ fontSize: 10, color: C.cyan, fontWeight: 700 }}>{c}</span>
                    </div>
                  ))}
                </div>
              </div>
            </DragSec>
          );
          if (sid === 'sentiment') return (
            <DragSec key="sentiment" id="sentiment" title="Sentiment Analysis" color={C.green}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginBottom: 14 }}>
                <MB label="OVERALL"  value={overallLabel} color={overallColor} />
                <MB label="POSITIVE" value={`${stats.pos} (${(stats.pos / stats.paragraphs * 100).toFixed(1)}%)`} color={C.green} />
                <MB label="NEGATIVE" value={`${stats.neg} (${(stats.neg / stats.paragraphs * 100).toFixed(1)}%)`} color={C.red} />
              </div>
              <p style={{ fontSize: 12, color: C.t2, lineHeight: 1.8, margin: 0 }}>
                Corpus sentiment is <strong style={{ color: overallColor }}>{overallLabel}</strong> (avg score: {stats.avgSent}).
                {stats.pos / stats.paragraphs > 0.5 && ' Predominantly positive — optimistic or promotional tone.'}
                {stats.neg / stats.paragraphs > 0.5 && ' Predominantly negative — complaints, critiques, or issue reports.'}
              </p>
            </DragSec>
          );
          if (sid === 'readability') return (
            <DragSec key="readability" id="readability" title="Readability" color={C.violet}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 8 }}>
                <MB label="FLESCH"   value={stats.rd.flesch}   color={stats.rd.flesch > 60 ? C.green : stats.rd.flesch > 40 ? C.gold : C.red} sub={stats.rd.grade} />
                <MB label="FK GRADE" value={stats.rd.fkGrade}  color={C.cyan} />
                <MB label="FOG"      value={stats.rd.fog}      color={C.violet} />
                <MB label="SMOG"     value={stats.rd.smog}     color={C.gold} />
              </div>
            </DragSec>
          );
          if (sid === 'entities') return (
            <DragSec key="entities" id="entities" title="Named Entities" color={C.gold}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 8 }}>
                {[['PERSON', C.cyan], ['ORG', C.green], ['LOCATION', C.gold], ['DATE', C.violet], ['MONEY', C.orange]].map(([t, c]) => (
                  <MB key={t} label={t} value={(stats.ents[t] || []).length} color={c} />
                ))}
              </div>
            </DragSec>
          );
          if (sid === 'topics') return (
            <DragSec key="topics" id="topics" title="Topic Modeling" color={C.gold}>
              <p style={{ fontSize: 12, color: C.t2, lineHeight: 1.8, margin: 0 }}>Run the <strong style={{ color: C.gold }}>TOPICS</strong> tab to extract latent clusters. Topics auto-labeled via co-occurrence analysis.</p>
            </DragSec>
          );
          if (sid === 'charts') return (
            <DragSec key="charts" id="charts" title="Pinned Charts" color={C.pink}>
              {!(dashboardCards || []).length
                ? <div style={{ padding: 20, textAlign: 'center', color: C.t4, background: C.raised, borderRadius: 4, border: `2px dashed ${C.b1}` }}>
                    <div style={{ fontSize: 20, marginBottom: 6, opacity: 0.3 }}>★</div>
                    <div style={{ fontSize: 11 }}>No charts pinned. Use ☆ on any chart across all NLP tabs.</div>
                  </div>
                : <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
                    {(dashboardCards || []).map(c => (
                      <div key={c.id} style={{ padding: '10px 14px', background: C.raised, border: `1px solid ${C.pink}33`, borderRadius: 4, borderTop: `2px solid ${C.pink}` }}>
                        <div style={{ fontSize: 10, color: C.pink, fontFamily: 'JetBrains Mono,monospace', fontWeight: 700 }}>{c.chart?.title || c.id}</div>
                        {c.chart?.subtitle && <div style={{ fontSize: 9, color: C.t4, marginTop: 2 }}>{c.chart.subtitle}</div>}
                      </div>
                    ))}
                  </div>
              }
            </DragSec>
          );
          if (sid === 'conclusion') return (
            <DragSec key="conclusion" id="conclusion" title="Insights & Next Steps" color={C.green} req>
              <p style={{ fontSize: 12, color: C.t2, lineHeight: 1.8, margin: '0 0 14px' }}>
                Analysis of <strong style={{ color: C.pink }}>{col}</strong> complete. <strong style={{ color: C.cyan }}>{stats.paragraphs} documents</strong> · {stats.unique} unique terms · {(stats.lexDiv * 100).toFixed(1)}% lexical density · {stats.lang} · <strong>{stats.rd.grade}</strong> readability · <strong style={{ color: overallColor }}>{overallLabel}</strong> sentiment.
              </p>
              <div style={{ padding: '12px 16px', background: C.raised, border: `1px solid ${C.green}33`, borderRadius: 4, marginBottom: 14 }}>
                <div style={{ fontSize: 9, color: C.green, fontFamily: 'JetBrains Mono,monospace', fontWeight: 700, marginBottom: 8 }}>RECOMMENDED NEXT STEPS</div>
                <ul style={{ margin: 0, paddingLeft: 16, fontSize: 11, color: C.t2, lineHeight: 2 }}>
                  <li>Run <strong style={{ color: C.gold }}>TOPICS</strong> tab — extract latent themes from this corpus.</li>
                  {stats.lexDiv > 0.6 && <li>High lexical density ({(stats.lexDiv * 100).toFixed(0)}%) — consider TF-IDF vectorization for downstream ML.</li>}
                  {stats.neg / stats.paragraphs > 0.3 && <li>Significant negative sentiment — explore negative docs for root-cause patterns.</li>}
                  <li>Use <strong style={{ color: C.blue }}>SIMILARITY</strong> to find near-duplicates and cluster related documents.</li>
                  <li>Check <strong style={{ color: C.violet }}>READABILITY</strong> — {stats.rd.grade} text may need adjustment for your target audience.</li>
                </ul>
              </div>
              {notes.length > 0 && (
                <div>
                  <div style={{ fontSize: 9, color: C.gold, fontFamily: 'JetBrains Mono,monospace', fontWeight: 700, marginBottom: 10 }}>RESEARCH NOTES ({notes.length})</div>
                  {notes.map(note => {
                    const tm = ntag(note.tag);
                    return (
                      <div key={note.id} style={{ background: C.surface, border: `1px solid ${tm.color}33`, borderLeft: `3px solid ${tm.color}`, borderRadius: 3, padding: '10px 13px', marginBottom: 8 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 5 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                            <span style={{ fontSize: 8, color: tm.color, fontFamily: 'JetBrains Mono,monospace', fontWeight: 700 }}>{tm.label}</span>
                            <span style={{ fontSize: 8, color: C.t4 }}>{new Date(note.ts).toLocaleString('en', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}</span>
                          </div>
                          <button onClick={() => setNotes(p => p.filter(n => n.id !== note.id))} style={{ background: 'none', border: 'none', color: C.red, cursor: 'pointer', fontSize: 14 }}>×</button>
                        </div>
                        <div style={{ fontSize: 12, color: C.t1, lineHeight: 1.75 }}>{note.text}</div>
                      </div>
                    );
                  })}
                </div>
              )}
            </DragSec>
          );
          return null;
        })}
      </div>
      {modal && <NoteModal onSave={n => setNotes(p => [...p, n])} onClose={() => setModal(false)} />}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// ROOT — NLPLab receives subTab from the parent hub, NO internal nav
// ─────────────────────────────────────────────────────────────────────────────
export default function NLPLab({ ds, subTab = 'setup', experiment, onUpdate, dashboardCards }) {
  const [config, setConfig] = useState(() => experiment?.nlpConfig || {
    primaryCol: '', secondaryCol: '', useStopwords: true, sampleSize: 500,
  });

  const onConfigChange = useCallback(patch => {
    setConfig(prev => { const next = { ...prev, ...patch }; onUpdate?.({ nlpConfig: next }); return next; });
  }, [onUpdate]);

  const texts = useMemo(() => {
    if (!ds || !config.primaryCol) return [];
    return ds.rows.slice(0, config.sampleSize).map(r => String(r[config.primaryCol] || '').trim()).filter(t => t.length > 0);
  }, [ds, config.primaryCol, config.sampleSize]);

  const rows = useMemo(() => ds ? ds.rows.slice(0, config.sampleSize) : [], [ds, config.sampleSize]);

  if (!ds) return (
    <div style={{ padding: 60, textAlign: 'center', color: C.t4, fontFamily: 'JetBrains Mono,monospace', fontSize: 11 }}>
      <div style={{ fontSize: 32, marginBottom: 12, opacity: 0.3 }}>◉</div>
      <div>Load a dataset first — use the DATA SOURCE tab.</div>
    </div>
  );

  const shared = { texts, rows, col: config.primaryCol, config, ds, experiment, dashboardCards };

  return (
    <>
      {subTab === 'setup'       && <SetupTab       ds={ds} config={config} onConfigChange={onConfigChange} />}
      {subTab === 'stats'       && <StatsTab        {...shared} />}
      {subTab === 'frequency'   && <FrequencyTab    {...shared} />}
      {subTab === 'readability' && <ReadabilityTab  {...shared} />}
      {subTab === 'sentiment'   && <SentimentTab    {...shared} />}
      {subTab === 'entities'    && <EntitiesTab     {...shared} />}
      {subTab === 'keywords'    && <KeywordsTab     {...shared} />}
      {subTab === 'topics'      && <TopicsTab       {...shared} />}
      {subTab === 'summary'     && <SummaryTab      {...shared} />}
      {subTab === 'similarity'  && <SimilarityTab   {...shared} />}
      {subTab === 'report'      && <ReportTab       {...shared} />}
    </>
  );
}
